/* eslint-disable require-jsdoc */
import {
  AutoClosingDropdown,
  FormSection,
  ModalTypeEnum,
  SecondaryPageTitle,
  PrimaryButton,
  TextArea,
  useAlert,
  useModal,
  TextInput,
  CheckIcon,
  CancelIcon,
  EditIcon,
} from "@wit/mpesa-ui-components";
import WarningIcon from "../../../../shared/icons/warning.icon";
import { FormSectionRow } from "@wit/mpesa-ui-components/lib/components/form-section/form-section.component";
import * as Yup from "yup";
import { DropdownType } from "@wit/mpesa-ui-components/lib/components/dropdown/dropdown.component";
import { LoadingText } from "../../../../shared/shared.styled";
import styleTheme from "@wit/mpesa-ui-components/lib/configs/theme.config";
import { AlertTypeEnum } from "@wit/mpesa-ui-components/lib/context/alert/alert.context";
import { Formik, FormikProps, FormikValues } from "formik";
import i18next, { TFunction } from "i18next";
import moment from "moment";
import React, { useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { IStoreInterface } from "../../../../configs/store.config";
import { RoutesEnum } from "../../../../routes/routes.constants";
import { PageContainer } from "../../../../shared/shared.styled";
import {
  IClientDocSubmissionDetailsRequest,
  IClientDocSubmissionChangeRequest,
  IClientDocSubmissionDetailsResponse,
  ImageType,
  ICustomReasonCharactersLimit,
  IDocValidationDetailsPageProps,
  IDocValidationDetailsImagesReducerInterface,
  IDocValidationAdditionalImagesReducerInterface,
  IDocValidationDetailsReducerInterface,
  IClientFormSubmissionChangeRequest,
} from "../doc-validation-details.interface";
import { ClientDocSubmissionNextStatusEnumKeys, ClientDocSubmissionNextStatusEnum } from "../../ekyc.shared.enums";
import DocValidationDetailsApi from "../doc-validation-details.api";
import { DocValidationDetailsActions } from "../doc-validation-details.store";
import { DocValidationDetailsUtils } from "../doc-validation-details.utils";
import { DocValidationUtils } from "../../doc-validation/doc-validation.utils";
import ClientDocPhotos from "../components/doc-validation-additional-photos.component";
import ClientAdditionalDocPhotos from "../components/doc-validation-additional-photos.component";
import DocStatusHistoryList from "../components/doc-status-history.component";
import DocRiskRatingHistoryList from "../components/doc-risk-rating-history.component";
import { useHistory, useLocation } from "react-router-dom";
import { ILoggedUser } from "../../../authentication/authentication.interfaces";
import DocStatusItem from "../components/doc-status-item.component";
import ExclamationIcon from "@wit/mpesa-ui-components/lib/assets/icons/exclamation.icon";
import { renderDate } from "../../../../shared/shared.utils";
import FullScreenImageModal from "../components/doc-validation-fullscreen-image.component";
import { UserRolesDefault } from "../../../admin/users/users.interfaces";
import DocTaskHistoryList from "../components/doc-task-history.component";
import {
  PROVINCES,
  townsDescription,
  NATIONALITIES,
  INCOME_RANGES,
  RISK_RATINGS,
  IDCARDISSUEPLACE,
} from "../../../../shared/constants/Nationalities";
import { validateDate, validateIdCardIssuingDate } from "../../../../shared/shared.utils";
import { IIdentityDocument } from "../../../../shared/models/identity.model";
import Occupations from "../../../../shared/utils/occupations";
import { EFieldStatus } from "../doc-validation-details.interface";
import { normalizeErrorMessage } from "../../../../shared/utils/errorMessageHelper.utils";
import { AxiosError } from "axios";
import { cleanBackendMessage } from "../../../../shared/utils/cleanBackendErrorMessage.utils";
import { requiredIfMandatory, isFieldMissingIfMandatory } from "../../../../shared/utils/KYCConfigs";

const DocValidationDetailsPage = () => {
  const ANOTHER_USER_SEEING_MINUTES = 10;
  const CUSTOM_REASON_CHARACTERS_LIMIT: ICustomReasonCharactersLimit = {
    max: 60,
    min: 4,
  };
  const history = useHistory();
  const { state } = useLocation<IDocValidationDetailsPageProps>();
  const remoteId = state?.remoteId;

  if (!remoteId) {
    history.push("/404");
  }
  // Hooks init
  const { t, i18n } = useTranslation("private");
  const isFrench = i18n.language.toLowerCase() === "fr";
  const [tMarket] = useTranslation("market_values");
  const dispatch = useDispatch();
  const [showAlert, hideAlert, setAlertProps] = useAlert();
  const formRowRef = useRef<HTMLDivElement>(null);
  const registrationApprovalRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<FormikProps<FormikValues>>(null);
  const agentInfoRef = useRef<HTMLDivElement>(null);
  const additionalInfo = useRef<HTMLDivElement>(null);
  const [firstOperator, setFirstOperator] = useState("");
  const [identities, setIdentities] = React.useState<IIdentityDocument[]>([]);
  const [occupationSelected, setOccupationSelected] = useState(false);
  const timeoutRef = useRef<any>(null);

  const idCardsRaw = ((i18next.services.resourceStore.data?.[i18next.language]?.market_values as any)?.idCards ??
    {}) as Record<string, string>;

  const { role } = useSelector((state: IStoreInterface) => state.authenticationReducer.loggedUser) as ILoggedUser;
  // Redux state
  const detailsData: IDocValidationDetailsReducerInterface = useSelector(
    (store: IStoreInterface) => store.docValidationDetailsReducer,
  );
  const user = useSelector((s: IStoreInterface) => s.authenticationReducer.loggedUser as ILoggedUser);
  React.useEffect(() => {
    if (detailsData?.changeHistories?.length > 0) {
      const operator = detailsData.changeHistories[0]?.operator || "";
      setFirstOperator(operator);
    }
  }, [detailsData]);
  // const firstOperator = detailsData.changeHistories?.[0]?.operator || "";

  const allowedStatusCodes = ["11", "20"];
  const isEditableCheck =
    role !== "view_only" &&
    (allowedStatusCodes.includes(detailsData.statusCode ?? "") ||
      (detailsData.statusCode === "25" && firstOperator === user.username));

  const submissionImages: IDocValidationDetailsImagesReducerInterface[] = useSelector(
    (store: IStoreInterface) => store.docValidationDetailsImagesReducer,
  );
  const additionalImages: IDocValidationAdditionalImagesReducerInterface[] = useSelector(
    (store: IStoreInterface) => store.docValidationAdditionalImagesReducer,
  );
  const navigationParams: any = {};

  // Local state definitions
  const [isLoadingDetails, setIsLoadingDetails] = React.useState(true);
  const [isLoadingImages, setIsLoadingImages] = React.useState(true);
  const [isEditable, setIsEditable] = React.useState(false);
  const [hasLoadFailed, setHasLoadFailed] = React.useState(false);
  const [isSubmittingReq, setIsSubmittingReq] = React.useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isEditModeClicked, setIsEditModeClicked] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [showNextDocStatus, setShowNextDocStatus] = useState(true);
  const [searchNationality, setSearchNationality] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [searchOccupation, setSearchOccupation] = useState("");
  const [isOccupationFocused, setIsOccupationFocused] = useState(false);
  const [requestDetailsParams, setRequestDetailsParams] = React.useState<IClientDocSubmissionDetailsRequest>({
    remoteId: remoteId,
    username: user.username,
    previousDuplicates: [],
  });

  interface IOccupation {
    KEY: string;
    EN: string;
    FR: string;
  }

  const OCCUPATIONS_MAP: Record<string, IOccupation> = Occupations;

  const OCCUPATIONS = Object.values(OCCUPATIONS_MAP).map(o => ({
    key: String(o.KEY),
    label: i18n.language === "fr" ? o.FR : o.EN,
    frLabel: o.FR,
  }));

  const filteredNationalities = searchNationality.trim()
    ? NATIONALITIES.filter(n => n.description.toLowerCase().includes(searchNationality.toLowerCase()))
    : NATIONALITIES;
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const filteredOccupations = useMemo(() => {
    if (!searchOccupation) {
      return OCCUPATIONS;
    }
    return OCCUPATIONS.filter(o =>
      (i18n.language === "fr" ? o.frLabel : o.label).toLowerCase().includes(searchOccupation.toLowerCase()),
    );
  }, [searchOccupation, i18n.language]);

  function getStatusReason(values: any): string {
    return `statusReasons.${values.nextStatus}.${values.nextStatusReason}`;
  }

  React.useEffect(() => {
    getClientSubmissionDetails();
  }, [requestDetailsParams]);

  React.useEffect(() => {}, [identities]);
  const [, , pictureModalProps] = useModal(
    ModalTypeEnum.CustomModal,
    undefined,
    <FullScreenImageModal photoUrl={detailsData.signatureUrl || ""} />,
  );

  React.useEffect(() => {
    pictureModalProps({ modalStyles: { width: "auto", overflow: "visible" } });
  }, [pictureModalProps]);

  React.useEffect(() => {
    let duplicateText = "";
    if (!isLoadingDetails) {
      const docNextStates = DocValidationDetailsUtils.getNextDocStatus(detailsData.status);
      const editable = docNextStates.length > 0;
      if (editable) {
        if (!i18next.exists("market_values:genders.0")) {
          setIsEditable(false);
          setAlertProps({
            title: t("pages.docSubmissionDetails.loadMarketValuesError"),
            type: AlertTypeEnum.ERROR,
          });
          showAlert();
          return;
        } else if (user.role === UserRolesDefault.VIEW_ONLY) {
          setIsEditable(false);
          return;
        }
        setIsEditable(true);
      }
      if (detailsData.numberOfIdNumberDuplications > 0) {
        duplicateText = `${detailsData.numberOfIdNumberDuplications} ${t(
          "pages.docSubmissionDetails.filters.byIdNumber",
        )}`;
      }
      if (detailsData.numberOfPhoneNumberDuplications > 0) {
        if (duplicateText) {
          duplicateText += ", ";
        }
        duplicateText += `${detailsData.numberOfPhoneNumberDuplications} ${t(
          "pages.docSubmissionDetails.filters.byPhoneNumber",
        )}`;
      }
      if (duplicateText) {
        setAlertProps({
          title: t("pages.docSubmissionDetails.filters.duplicatedRecords"),
          content: duplicateText,
          type: AlertTypeEnum.WARNING,
        });
        showAlert();
      }
    }
  }, [detailsData]);

  React.useLayoutEffect(() => {
    const registrationStatusSection = formRowRef?.current?.parentElement?.parentElement;
    registrationStatusSection?.classList?.add("status-container");
    const registrationApprovalSection = registrationApprovalRef?.current?.parentElement?.parentElement?.parentElement;
    registrationApprovalSection?.classList?.add("status-container");

    const agentInfoSection = agentInfoRef?.current?.parentElement?.parentElement;
    agentInfoSection?.classList?.add("status-container");
  }, [formRowRef.current, registrationApprovalRef.current, agentInfoRef.current]);

  /*
   * Get client doc submission images
   */
  const getImages = async (requestId: string, documentType: string) => {
    const imagesType: ImageType[] = [ImageType.FRONT, ImageType.SELFIE];
    const matchedIdentity = identities.find(d => d.documentId === documentType);
    if (matchedIdentity?.captureBackSide === true) {
      imagesType.push(ImageType.BACK);
    }
    const promises: Promise<any>[] = [];
    imagesType.forEach((imageType: ImageType) => {
      promises.push(
        new Promise((resolve, _) => {
          DocValidationDetailsApi.methods
            .getImageSubmissionDetails({
              submissionRequestId: requestId,
              imageType,
            })
            .then(res => {
              const imageResponse: string = res.data.content;
              resolve({
                url: imageResponse,
                extension: "jpeg",
                context: imageType,
              });
            })
            .catch((error: any) => {
              let backendMessage = error?.data?.status?.message || t("pages.docSubmissionDetails.getDetailsError");
              backendMessage = cleanBackendMessage(backendMessage);
              const httpCode = error?.data?.status?.httpCode;
              backendMessage = normalizeErrorMessage(backendMessage, httpCode);
              setHasLoadFailed(true);
              setAlertProps({
                title: backendMessage,
                type: AlertTypeEnum.ERROR,
              });
              showAlert();
            });
        }),
      );
    });
    const images = await Promise.all(promises);
    setIsLoadingImages(false);
    dispatch(DocValidationDetailsActions.creators.fetchDocDetailsImagesSuccessAction(images));
  };

  const mapRiskToBackend = (value?: string | number): string => {
    const val = String(value ?? "");
    switch (val) {
      case "1":
        return "LOW_RISK";
      case "2":
        return "MEDIUM_RISK";
      case "3":
        return "HIGH_RISK";
      default:
        return "";
    }
  };

  const getAdditionalImages = async (requestId: string) => {
    try {
      const res = await DocValidationDetailsApi.methods.getAdditionalImageSubmissionDetails({
        submissionRequestId: requestId,
        imageType: ImageType.ADDITIONAL_DOCUMENTS,
      });

      const additionalImages: { content: string }[] = res.data.additionalImages ?? [];
      const mappedImages: IDocValidationAdditionalImagesReducerInterface[] = additionalImages.map(img => ({
        url: img.content,
        context: "ADDITIONAL_DOCUMENTS",
        extension: "jpeg",
      }));
      dispatch(DocValidationDetailsActions.creators.fetchDocAdditionalImagesSuccessAction(mappedImages));
      setIsLoadingImages(false);
    } catch (error) {
      const err = error as AxiosError<any>;
      let backendMessage = err.response?.data?.status?.message || t("pages.docSubmissionDetails.getDetailsError");
      backendMessage = cleanBackendMessage(backendMessage);
      const httpCode = err.response?.data?.status?.httpCode;
      backendMessage = normalizeErrorMessage(backendMessage, httpCode);
      setHasLoadFailed(true);
      setAlertProps({
        title: backendMessage,
        type: AlertTypeEnum.ERROR,
      });
      showAlert();
      setIsLoadingImages(false);
    }
  };

  React.useEffect(() => {
    if (!identities || identities.length === 0) {
      console.log("Identity docs not avaialble yet.");
      return;
    }
    getImages(requestDetailsParams.remoteId, detailsData.documentType);
    getAdditionalImages(requestDetailsParams.remoteId);
  }, [identities]);

  const getClientSubmissionDetails = () => {
    setIsLoadingDetails(true);
    DocValidationDetailsApi.methods
      .getClientSubmissionDetails(requestDetailsParams)
      .finally(() => setIsLoadingDetails(false))
      .then(res => {
        const detailsResponse: IClientDocSubmissionDetailsResponse = res.data;
        getImages(requestDetailsParams.remoteId, detailsResponse.documentType);
        getAdditionalImages(requestDetailsParams.remoteId);
        dispatch(DocValidationDetailsActions.creators.fetchDocDetailsSuccessAction(detailsResponse));
        DocValidationDetailsApi.methods.getDocuments().then(identitiesRes => {
          const allDocs = identitiesRes.data.identityDocuments || [];
          const activeDocs = allDocs.filter(doc => doc.status?.toLowerCase() === "active");
          setIdentities(activeDocs);
        });
      })
      .catch((error: any) => {
        let backendMessage = error?.data?.status?.message || t("pages.docSubmissionDetails.getDetailsError");
        backendMessage = cleanBackendMessage(backendMessage);
        const httpCode = error?.data?.status?.httpCode;
        backendMessage = normalizeErrorMessage(backendMessage, httpCode);
        setHasLoadFailed(true);
        setAlertProps({
          title: backendMessage,
          type: AlertTypeEnum.ERROR,
        });
        showAlert();
      });
  };

  // form update function new addition
  const saveFormChanges = (values: any) => {
    const idNumber = values.idNumber?.trim();
    if (!idNumber || idNumber.length < 2 || idNumber.length > 12) {
      setAlertProps({
        title:
          t("pages.docSubmissionDetails.formErrors.invalidIdNumber") ||
          "ID number must be between 2 and 12 characters.",
        type: AlertTypeEnum.ERROR,
      });
      showAlert();
      return;
    }
    if (!values.comment || values.comment.trim() === "") {
      setAlertProps({
        title: t("pages.docSubmissionDetails.commentRequired"),
        type: AlertTypeEnum.ERROR,
      });
      showAlert();
      return;
    }

    const fieldValueMap: Record<string, any> = {
      INCOME: values.income,
      OCCUPATION: values.occupationKey || values.occupation,
      ID_CARD_ISSUING_BY: values.idCardIssuingBy,
      ID_CARD_ISSUING_DATE: values.idCardIssuingDate,
      ID_CARD_ISSUING_PLACE: values.idCardIssuingPlace,
    };

    for (const field of Object.keys(fieldValueMap)) {
      if (isFieldMissingIfMandatory(field, fieldValueMap[field])) {
        const translationKey = `pages.docSubmissionDetails.formErrors.${field.toLowerCase()}.required`;
        const translatedTitle = t(translationKey, { defaultValue: `${field.replace(/_/g, " ")} is required` });
        setAlertProps({
          title: translatedTitle,
          type: AlertTypeEnum.ERROR,
        });
        showAlert();
        return;
      }
    }

    const originalOccupationKey = detailsData.occupation?.value || "";
    const originalOccupationValue =
      Occupations[originalOccupationKey as keyof typeof Occupations]?.EN || originalOccupationKey;
    const selectedOccupationKey = values.occupationKey || originalOccupationKey;
    const selectedOccupationValue =
      selectedOccupationKey && Occupations[selectedOccupationKey as keyof typeof Occupations]
        ? Occupations[selectedOccupationKey as keyof typeof Occupations].EN
        : originalOccupationValue;

    const occupationStatus = selectedOccupationValue !== originalOccupationValue ? "EDITED" : "PENDING";

    const originalIncomeValue = detailsData.income?.value || "";
    const selectedIncomeValue = values.income || originalIncomeValue;
    const incomeStatus = selectedIncomeValue !== originalIncomeValue ? "EDITED" : "PENDING";

    const normalizeDate = (date?: string | number | Date | null) => {
      if (!date) {
        return "";
      }
      if (typeof date === "number") {
        return moment(date).format("DD/MM/YYYY");
      }
      const m = moment(date, ["DD/MM/YYYY", "YYYY-MM-DD", moment.ISO_8601], true);
      return m.isValid() ? m.format("DD/MM/YYYY") : "";
    };

    const dobOriginalRaw = detailsData.dateOfBirth?.value;
    const dobCurrentRaw = values.dateOfBirth;
    const dobOriginal = normalizeDate(dobOriginalRaw);
    const dobCurrent = normalizeDate(dobCurrentRaw);
    const isDobChanged =
      moment(dobOriginal, "DD/MM/YYYY", true).isValid() &&
      moment(dobCurrent, "DD/MM/YYYY", true).isValid() &&
      !moment(dobOriginal, "DD/MM/YYYY").isSame(moment(dobCurrent, "DD/MM/YYYY"));
    let dateOfBirthStatus: EFieldStatus;

    if (isDobChanged) {
      dateOfBirthStatus = EFieldStatus.EDITED;
    } else {
      dateOfBirthStatus = detailsData.dateOfBirth?.status ?? EFieldStatus.REVIEW;
    }
    const request: IClientFormSubmissionChangeRequest = {
      firstName: {
        value: values.firstName || detailsData.firstName?.value,
        status:
          values.firstName && values.firstName !== detailsData.firstName?.value
            ? "EDITED"
            : detailsData.firstName?.status ?? "",
      },
      lastName: {
        value: values.lastName || detailsData.lastName?.value,
        status:
          values.lastName && values.lastName !== detailsData.lastName?.value
            ? "EDITED"
            : detailsData.lastName?.status ?? "",
      },
      dateOfBirth: {
        value: dobCurrent || dobOriginal,
        status: dateOfBirthStatus,
      },
      placeOfBirth: {
        value: values.placeOfBirth || detailsData.placeOfBirth?.value,
        status:
          values.placeOfBirth && values.placeOfBirth !== detailsData.placeOfBirth?.value
            ? "EDITED"
            : detailsData.placeOfBirth?.status ?? "",
      },
      gender: {
        value: values.gender || detailsData.gender?.value,
        status:
          values.gender && values.gender !== detailsData.gender?.value ? "EDITED" : detailsData.gender?.status ?? "",
      },
      idNumber: {
        value: values.idNumber || detailsData.idNumber?.value,
        status:
          values.idNumber && values.idNumber !== detailsData.idNumber?.value
            ? "EDITED"
            : detailsData.idNumber?.status ?? "",
      },
      nationality: {
        value: values.nationality || detailsData.nationality?.value,
        status:
          values.nationality && values.nationality !== detailsData.nationality?.value
            ? "EDITED"
            : detailsData.nationality?.status ?? "",
      },
      occupation: {
        value: selectedOccupationValue,
        status: occupationStatus,
      },
      income: {
        value: selectedIncomeValue,
        status: incomeStatus,
      },
      riskRating: {
        value: mapRiskToBackend(values.riskRating) || mapRiskToBackend(detailsData.riskRating?.value),
        status:
          values.riskRating && values.riskRating !== detailsData.riskRating?.value
            ? "EDITED"
            : detailsData.riskRating?.status ?? "",
      },
      idCardIssuingBy: {
        value: values.idCardIssuingBy || detailsData.idCardIssuingBy?.value,
        status:
          values.idCardIssuingBy && values.idCardIssuingBy !== detailsData.idCardIssuingBy?.value
            ? "EDITED"
            : detailsData.idCardIssuingBy?.status ?? "",
      },

      idCardIssuingDate: {
        value: normalizeDate(values.idCardIssuingDate) || normalizeDate(detailsData.idCardIssuingDate?.value),
        status:
          values.idCardIssuingDate &&
          normalizeDate(values.idCardIssuingDate) !== normalizeDate(detailsData.idCardIssuingDate?.value)
            ? "EDITED"
            : detailsData.idCardIssuingDate?.status ?? "",
      },
      idCardIssuingPlace: {
        value: values.idCardIssuingPlace || detailsData.idCardIssuingPlace?.value,
        status:
          values.idCardIssuingPlace && values.idCardIssuingPlace !== detailsData.idCardIssuingPlace?.value
            ? "EDITED"
            : detailsData.idCardIssuingPlace?.status ?? "",
      },
      street: {
        value: values.addressStreet || detailsData.addressStreet?.value,
        status:
          values.addressStreet && values.addressStreet !== detailsData.addressStreet?.value
            ? "EDITED"
            : detailsData.addressStreet?.status ?? "",
      },
      province: {
        value: values.addressProvince || detailsData.addressProvince?.value,
        status:
          values.addressProvince && values.addressProvince !== detailsData.addressProvince?.value
            ? "EDITED"
            : detailsData.addressProvince?.status ?? "",
      },
      town: {
        value: values.addressTown || detailsData.addressTown?.value,
        status:
          values.addressTown && values.addressTown !== detailsData.addressTown?.value
            ? "EDITED"
            : detailsData.addressTown?.status ?? "",
      },
      commune: {
        value: values.addressCommune || detailsData.addressCommune?.value,
        status:
          values.addressCommune && values.addressCommune !== detailsData.addressCommune?.value
            ? "EDITED"
            : detailsData.addressCommune?.status ?? "",
      },
      operator: user.username,
      comment: values.comment,
      documentType: values.idTypeKey || detailsData.documentType,
    };

    const filteredRequest = Object.fromEntries(
      Object.entries(request).filter(([key, val]) => {
        if (key === "occupation" || key === "income") {
          return true;
        }
        if (typeof val === "object" && val !== null && "value" in val && "status" in val) {
          return val.value?.trim() || val.status?.trim();
        }
        return true;
      }),
    ) as IClientFormSubmissionChangeRequest;

    DocValidationDetailsApi.methods
      .changeUpdateClientSubmissionDetails(remoteId, filteredRequest)
      .finally(() => setIsSubmittingReq(false))
      .then(() => {
        history.push({
          pathname: RoutesEnum.DOC_VALIDATION,
          state: {
            successfulFormUpdate: true,
          },
        });
      })
      .catch((error: any) => {
        let backendMessage = error?.data?.status?.message || t("pages.docSubmissionDetails.changeFormError");
        backendMessage = cleanBackendMessage(backendMessage);
        const httpCode = error?.data?.status?.httpCode;
        backendMessage = normalizeErrorMessage(backendMessage, httpCode);
        setHasLoadFailed(true);
        setAlertProps({
          title: backendMessage,
          type: AlertTypeEnum.ERROR,
        });
        showAlert();
      });
  };

  const getDocumentLabel = (docType: string) => {
    const matchedDoc = identities.find(d => d.documentId === docType);
    if (!matchedDoc) {
      return docType;
    }
    return isFrench ? matchedDoc.fr : matchedDoc.en;
  };

  const getRiskDescription = (value?: string | number) => {
    if (!value) {
      return "";
    }
    const risk = RISK_RATINGS.find(r => String(r.id) === String(value));
    return risk?.description || "";
  };

  const getOccupationEnglishValue = (occupationKey: string) => {
    const selected = Occupations[occupationKey as keyof typeof Occupations];
    return selected ? selected.EN : occupationKey;
  };

  const saveChanges = (values: any) => {
    setIsSubmittingReq(true);
    const request: IClientDocSubmissionChangeRequest = {
      status: ClientDocSubmissionNextStatusEnum[values.nextStatus as ClientDocSubmissionNextStatusEnumKeys],
    };
    const stateReason = getReasonParamsFromValues(values.reasonText, values.nextStatusReason, values.nextStatus);
    if (stateReason) {
      request.comment = stateReason.comment;
      request.customComment = stateReason.customComment;
    }
    DocValidationDetailsApi.methods
      .changeClientSubmissionDetails(remoteId, request)
      .finally(() => setIsSubmittingReq(false))
      .then(() => {
        history.push({
          pathname: RoutesEnum.DOC_VALIDATION,
          state: {
            successfulUpdate: true,
          },
        });
      })
      .catch((error: any) => {
        let backendMessage = error?.data?.status?.message || t("pages.docSubmissionDetails.changeDetailsError");
        backendMessage = cleanBackendMessage(backendMessage);
        const httpCode = error?.data?.status?.httpCode;
        backendMessage = normalizeErrorMessage(backendMessage, httpCode);
        setHasLoadFailed(true);
        setAlertProps({
          title: backendMessage,
          type: AlertTypeEnum.ERROR,
        });
        showAlert();
      });
  };

  const getReasonParamsFromValues = (reasonText?: string, reasonType?: string, status?: string): any => {
    if (reasonType === "CUSTOM" && reasonText) {
      return {
        comment: tMarket(`statusReasons.${status}.${reasonType}`),
        customComment: reasonText,
      };
    }

    if (reasonType) {
      return {
        comment: tMarket(`statusReasons.${status}.${reasonType}`),
      };
    }

    return null;
  };

  const getFormValidationSchema = (t: TFunction) => {
    return Yup.object().shape({
      nextStatus: Yup.string().required(t("pages.docSubmissionDetails.formErrors.required")),
      nextStatusReason: Yup.string().when("nextStatus", {
        is: (value: ClientDocSubmissionNextStatusEnumKeys) => {
          return DocValidationDetailsUtils.nextDocStatusHasReason(value);
        },
        then: Yup.string().required(t("pages.docSubmissionDetails.formErrors.required")),
      }),
      reasonText: Yup.string().when("nextStatusReason", {
        is: "CUSTOM",
        then: Yup.string().required("error"),
      }),
      dateOfBirth: Yup.string()
        .required(t("pages.docSubmissionDetails.formErrors.required"))
        .test("isValidDate", t("pages.docSubmissionDetails.formErrors.invalidDobFormat"), value => {
          const validation = validateDate(value || "");
          return validation.isValid;
        }),
      idCardIssuingDate: Yup.string()
        .required(t("pages.docSubmissionDetails.formErrors.required"))
        .test("isValidIdIssueDate", t("pages.docSubmissionDetails.formErrors.invalidIdDate"), function(value) {
          const { dateOfBirth } = this.parent;
          const result = validateIdCardIssuingDate(dateOfBirth, value || "");
          return result.isValid;
        }),
      idNumber: Yup.string()
        .required(t("pages.docSubmissionDetails.formErrors.required"))
        .min(2, t("pages.docSubmissionDetails.formErrors.minLength", { min: 2 }))
        .max(12, t("pages.docSubmissionDetails.formErrors.maxLength", { max: 12 })),
      firstName: Yup.string().required(t("pages.docSubmissionDetails.formErrors.required")),
      lastName: Yup.string().required(t("pages.docSubmissionDetails.formErrors.required")),
      placeOfBirth: Yup.string().required(t("pages.docSubmissionDetails.formErrors.required")),
      addressStreet: Yup.string().required(t("pages.docSubmissionDetails.formErrors.required")),
      addressCommune: Yup.string().required(t("pages.docSubmissionDetails.formErrors.required")),
      occupation: requiredIfMandatory("OCCUPATION", Yup.string()),
      income: requiredIfMandatory("INCOME", Yup.string()),
      idCardIssuingBy: requiredIfMandatory("ID_CARD_ISSUING_BY", Yup.string()),
      idCardIssuingPlace: requiredIfMandatory("ID_CARD_ISSUING_PLACE", Yup.string()),
      idCardIssuingDateDynamic: requiredIfMandatory(
        "ID_CARD_ISSUING_DATE",
        Yup.string().matches(/^\d{2}\/\d{2}\/\d{4}$/, t("Invalid date format (DD/MM/YYYY)")),
      ),
    });
  };

  const [values, setValues] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    placeOfBirth: "",
    gender: "",
    idNumber: "",
    nationality: "",
    occupation: "",
    income: "",
    riskRating: "",
    idCardIssuingBy: "",
    idCardIssuingDate: "",
    idCardIssuingPlace: "",
    documentType: "",
    phoneNumber: "",
    addressStreet: "",
    addressProvince: "",
    addressTown: "",
    addressCommune: "",
    comment: "",
  });
  const handleEditClick = (field: any, value: any) => {
    setEditMode(true);
    setIsEditModeClicked(true);
    setEditingField(field);
    setShowNextDocStatus(false);
    setValues(prevValues => ({
      ...prevValues,
      [field]: value,
    }));
  };
  const handleSave = (fieldName: string, value: any, setFieldValue: any, values: any) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    if (fieldName === "addressProvince") {
      const selectedProvince = PROVINCES.find(p => p.id === Number(value));
      if (selectedProvince) {
        setFieldValue(fieldName, selectedProvince.description);
      } else {
        setFieldValue(fieldName, value);
      }
    } else {
      setFieldValue(fieldName, value);
    }
    if (fieldName === "addressTown") {
      const selectedTowns = townsDescription.find(p => p.id === Number(value));
      if (selectedTowns) {
        setFieldValue(fieldName, selectedTowns.description);
      } else {
        setFieldValue(fieldName, value);
      }
    } else {
      setFieldValue(fieldName, value);
    }

    if (fieldName === "dateOfBirth") {
      const validation = validateDate(trimmedValue);
      if (!validation.isValid) {
        setAlertProps({
          title: t(validation.message || "pages.docSubmissionDetails.formErrors.invalidDobFormat"),
          type: AlertTypeEnum.ERROR,
        });
        showAlert();
        return;
      }

      setFieldValue(fieldName, trimmedValue);
    }
    if (fieldName === "idCardIssuingDate") {
      const dob = values?.dateOfBirth || "";
      const validation = validateIdCardIssuingDate(dob, trimmedValue);

      if (!validation.isValid) {
        setAlertProps({
          title: t(validation.message),
          type: AlertTypeEnum.ERROR,
        });
        showAlert();
        return;
      }

      setFieldValue(fieldName, trimmedValue);
    }
    setEditMode(false);
    setIsEditModeClicked(true);
    setEditingField(null);
  };

  const handleCancel = (fieldName?: string) => {
    if (!fieldName) {
      return;
    }

    let originalValue = "";
    let originalStatus = "";

    const field = (detailsData as any)[fieldName];

    if (field && typeof field === "object" && "value" in field) {
      const rawValue = field.value;
      originalStatus = field.status || "";

      if (moment(rawValue, ["YYYY-MM-DD", "DD/MM/YYYY"], true).isValid()) {
        originalValue = moment(rawValue, ["YYYY-MM-DD", "DD/MM/YYYY"]).format("DD/MM/YYYY");
      } else if (typeof rawValue === "number" || rawValue instanceof Date) {
        originalValue = moment(rawValue).format("DD/MM/YYYY");
      } else {
        originalValue = rawValue || "";
      }
    } else if (typeof field === "string" || typeof field === "number") {
      if (moment(field, ["YYYY-MM-DD", "DD/MM/YYYY"], true).isValid()) {
        originalValue = moment(field, ["YYYY-MM-DD", "DD/MM/YYYY"]).format("DD/MM/YYYY");
      } else {
        originalValue = String(field);
      }
    }

    setValues(prev => ({
      ...prev,
      [fieldName]: originalValue,
      [`${fieldName}Status`]: originalStatus,
    }));

    if (formRef?.current) {
      formRef.current.setFieldValue(fieldName, originalValue);
    }

    // Restore edit mode
    setEditMode(false);
    setEditingField(null);
  };
  return (
    <PageContainer style={{ paddingBottom: 140 }}>
      <SecondaryPageTitle
        goBackFn={() => {
          hideAlert();
          if (requestDetailsParams.previousDuplicates.length > 0) {
            setRequestDetailsParams((prev: IClientDocSubmissionDetailsRequest) => ({
              ...prev,
              previousDuplicates: prev.previousDuplicates.slice(0, -1),
              remoteId: prev.previousDuplicates[prev.previousDuplicates.length - 1],
            }));
          } else {
            setRequestDetailsParams({
              ...requestDetailsParams,
              previousDuplicates: [],
            });
            const currentPath = window.location.pathname;
            history.push({
              pathname: currentPath.startsWith("/task-validation")
                ? RoutesEnum.TASK_VALIDATION
                : RoutesEnum.DOC_VALIDATION,
              state: {
                successfulUpdate: false,
              },
            });
          }
        }}
        title={navigationParams.fullName || detailsData.fullName}
        displayInitialsCircle={true}
        titleFontSize={26}
      />
      {isEditable && (
        <ButtonsContainer>
          <ButtonWrapper>
            <PrimaryButton
              titleLabel={t("pages.docSubmissionDetails.buttonCancelLabel")}
              type="submit"
              redTheme={false}
              onClick={() => {
                hideAlert();
                window.history.back();
              }}
              disabled={isSubmittingReq}
            />
          </ButtonWrapper>
          <ButtonWrapper style={{ width: "auto" }}>
            {!isEditModeClicked ? (
              <PrimaryButton
                titleLabel={t("pages.docSubmissionDetails.buttonStatusLabel")}
                type="submit"
                disabled={isButtonDisabled}
                onClick={() => {
                  if (formRef.current) {
                    saveChanges(formRef.current.values);
                  } else {
                    setAlertProps({
                      title: t("pages.docSubmissionDetails.failedToSaveChanges"),
                      type: AlertTypeEnum.ERROR,
                    });
                    showAlert();
                  }
                }}
                redTheme={true}
                loading={isSubmittingReq}
              />
            ) : (
              <PrimaryButton
                titleLabel={t("pages.docSubmissionDetails.buttonFormLabel")}
                type="submit"
                disabled={isButtonDisabled}
                onClick={() => {
                  if (formRef.current) {
                    saveFormChanges(formRef.current.values);
                    setEditMode(false);
                  } else {
                    setAlertProps({
                      title: t("pages.docSubmissionDetails.failedToSaveChanges"),
                      type: AlertTypeEnum.ERROR,
                    });
                    showAlert();
                  }
                }}
                redTheme={true}
                loading={isSubmittingReq}
              />
            )}
          </ButtonWrapper>
        </ButtonsContainer>
      )}

      {!isLoadingDetails &&
      !hasLoadFailed &&
      isEditable &&
      detailsData &&
      ((detailsData.lastSeenBy &&
        detailsData.lastSeenBy !== user.username &&
        detailsData.lastSeenTime !== undefined &&
        detailsData.lastSeenTime > Date.now() - ANOTHER_USER_SEEING_MINUTES * 60 * 1000) ||
        (detailsData.penultSeenBy &&
          detailsData.penultSeenBy !== user.username &&
          detailsData.penultSeenTime !== undefined &&
          detailsData.penultSeenTime > Date.now() - ANOTHER_USER_SEEING_MINUTES * 60 * 1000)) ? (
        <WarningHeader>
          {t("pages.docSubmissionDetails.beingSeenByWarn", { minutes: ANOTHER_USER_SEEING_MINUTES })}
        </WarningHeader>
      ) : (
        <></>
      )}

      <PageContent>
        <MainContent>
          <Formik
            innerRef={formRef}
            initialValues={{
              nextStatus: undefined,
              nextStatusReason: undefined,
              reasonText: undefined,
              firstName: detailsData.firstName?.value || "",
              lastName: detailsData.lastName?.value || "",
              dateOfBirth: detailsData.dateOfBirth?.value
                ? moment(detailsData.dateOfBirth.value).format("DD/MM/YYYY")
                : "",
              placeOfBirth: detailsData.placeOfBirth?.value || "",
              gender: detailsData.gender?.value || "",
              documentType: detailsData.documentType || "",
              idType: detailsData.documentType
                ? isFrench
                  ? identities.find(d => d.documentType === detailsData.documentType)?.fr
                  : identities.find(d => d.documentType === detailsData.documentType)?.en
                : "",
              idTypeKey: detailsData.documentType || "",
              idNumber: detailsData.idNumber?.value || "",
              nationality: detailsData.nationality?.value || "",
              occupation: detailsData.occupation?.value || "",
              income: detailsData.income?.value || "",
              riskRating: detailsData.riskRating?.value || "",
              idCardIssuingBy: detailsData.idCardIssuingBy?.value || "",
              idCardIssuingDate: detailsData.idCardIssuingDate?.value
                ? moment(detailsData.idCardIssuingDate.value).format("DD/MM/YYYY")
                : "",
              idCardIssuingPlace: detailsData.idCardIssuingPlace?.value || "",
              addressStreet: detailsData.addressStreet?.value || "",
              addressProvince: detailsData.addressProvince?.value || "",
              addressTown: detailsData.addressTown?.value || "",
              addressCommune: detailsData.addressCommune?.value || "",
            }}
            enableReinitialize={true}
            validationSchema={getFormValidationSchema(t)}
            validateOnChange={true}
            validateOnBlur={false}
            onSubmit={() => {}}
            render={props => {
              const { values, handleChange, setFieldValue, handleSubmit } = props;
              var rows: FormSectionRow[];

              if (isLoadingDetails || hasLoadFailed) {
                rows = [];
              } else {
                rows = [
                  {
                    label: t("pages.docSubmissionDetails.firstName"),
                    displayComponent: detailsData.firstName?.value,
                    editingComponent: (
                      <ManualEntryWrapper>
                        {editingField === "firstName" || props.errors.firstName ? (
                          <EditableWrapper style={{ position: "relative", paddingBottom: "20px" }}>
                            <TextInput
                              name="firstName"
                              id={"firstName"}
                              maxLength={50}
                              onChange={e => handleChange(e)}
                              onBlur={props.handleBlur}
                              value={values.firstName}
                              style={{ width: "100%", paddingRight: "50px" }}
                            />
                            {props.errors.firstName && props.touched.firstName && (
                              <ValidationErrorFloating>{props.errors.firstName}</ValidationErrorFloating>
                            )}
                            <EditContainer>
                              <Edit
                                onClick={async () => {
                                  props.setFieldTouched("firstName", true);
                                  const errors = await props.validateForm();
                                  if (!errors.firstName) {
                                    handleSave("firstName", values.firstName, setFieldValue, values);
                                  }
                                }}
                              >
                                <SmallButtonIconContainer
                                  color={styleTheme.palette.vodafoneRed}
                                  style={{ marginRight: 5 }}
                                >
                                  <CheckIcon />
                                </SmallButtonIconContainer>
                              </Edit>

                              <Edit onClick={() => handleCancel("firstName")}>
                                <SmallButtonIconContainer
                                  color={styleTheme.palette.vodafoneRed}
                                  style={{ marginRight: 5 }}
                                >
                                  <CancelIcon />
                                </SmallButtonIconContainer>
                              </Edit>
                            </EditContainer>
                          </EditableWrapper>
                        ) : (
                          <NoEditableWrapper>
                            <div>
                              <NonEditableText>{values.firstName}</NonEditableText>
                            </div>
                            {isEditableCheck && (
                              <div>
                                <Edit
                                  onClick={() => {
                                    handleEditClick("firstName", values.firstName);
                                  }}
                                >
                                  <div style={{ position: "relative", left: "30px" }}>
                                    <SmallButtonIconContainer
                                      color={styleTheme.palette.vodafoneRed}
                                      style={{ marginRight: 5 }}
                                    >
                                      <EditIcon />
                                    </SmallButtonIconContainer>
                                  </div>
                                </Edit>
                              </div>
                            )}
                          </NoEditableWrapper>
                        )}

                        {detailsData.firstName?.status === "REVIEW" && (
                          <MessageWrapper>
                            <ManualEntryLabel>{t("pages.docSubmissionDetails.manualReview")}</ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.turquoiseBlue} />
                          </MessageWrapper>
                        )}
                        {detailsData.firstName?.status === "EDITED" && (
                          <MessageWrapper>
                            <ManualEntryLabel color={styleTheme.palette.warningYellow}>
                              {t("pages.docSubmissionDetails.updatedFields")}
                            </ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.warningYellow} />
                          </MessageWrapper>
                        )}
                      </ManualEntryWrapper>
                    ),
                  },
                  {
                    label: t("pages.docSubmissionDetails.lastName"),
                    displayComponent: detailsData.lastName?.value,
                    editingComponent: (
                      <ManualEntryWrapper>
                        {editingField === "lastName" || props.errors.lastName ? (
                          <EditableWrapper style={{ position: "relative", paddingBottom: "20px" }}>
                            <TextInput
                              name="lastName"
                              id={"lastName"}
                              maxLength={50}
                              onChange={e => handleChange(e)}
                              onBlur={props.handleBlur}
                              value={values.lastName}
                              style={{ width: "100%", paddingRight: "50px" }}
                            />
                            {props.errors.lastName && props.touched.lastName && (
                              <ValidationErrorFloating>{props.errors.lastName}</ValidationErrorFloating>
                            )}
                            <EditContainer style={{ position: "absolute", top: "30%" }}>
                              <Edit
                                onClick={async () => {
                                  props.setFieldTouched("lastName", true);
                                  const errors = await props.validateForm();
                                  if (!errors.lastName) {
                                    handleSave("lastName", values.lastName, setFieldValue, values);
                                  }
                                }}
                              >
                                <SmallButtonIconContainer
                                  color={styleTheme.palette.vodafoneRed}
                                  style={{ marginRight: 5 }}
                                >
                                  <CheckIcon />
                                </SmallButtonIconContainer>
                              </Edit>

                              <Edit onClick={() => handleCancel("lastName")}>
                                <SmallButtonIconContainer
                                  color={styleTheme.palette.vodafoneRed}
                                  style={{ marginRight: 5 }}
                                >
                                  <CancelIcon />
                                </SmallButtonIconContainer>
                              </Edit>
                            </EditContainer>
                          </EditableWrapper>
                        ) : (
                          <NoEditableWrapper>
                            <div>
                              <NonEditableText>{values.lastName}</NonEditableText>
                            </div>
                            {isEditableCheck && (
                              <div>
                                <Edit
                                  onClick={() => {
                                    handleEditClick("lastName", values.lastName);
                                  }}
                                >
                                  <div style={{ position: "relative", left: "30px" }}>
                                    <SmallButtonIconContainer
                                      color={styleTheme.palette.vodafoneRed}
                                      style={{ marginRight: 5 }}
                                    >
                                      <EditIcon />
                                    </SmallButtonIconContainer>
                                  </div>
                                </Edit>
                              </div>
                            )}
                          </NoEditableWrapper>
                        )}

                        {detailsData.lastName?.status === "REVIEW" && (
                          <MessageWrapper>
                            <ManualEntryLabel>{t("pages.docSubmissionDetails.manualReview")}</ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.turquoiseBlue} />
                          </MessageWrapper>
                        )}
                        {detailsData.lastName?.status === "EDITED" && (
                          <MessageWrapper>
                            <ManualEntryLabel color={styleTheme.palette.warningYellow}>
                              {t("pages.docSubmissionDetails.updatedFields")}
                            </ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.warningYellow} />
                          </MessageWrapper>
                        )}
                      </ManualEntryWrapper>
                    ),
                  },
                  {
                    label: t("pages.docSubmissionDetails.dateOfBirth"),
                    displayComponent: renderDate({
                      date: moment(detailsData.dateOfBirth?.value),
                      printHour: false,
                    }),
                    editingComponent: (
                      <ManualEntryWrapper>
                        {isEditableCheck && editMode && editingField === "dateOfBirth" ? (
                          <EditableWrapper>
                            <TextInput
                              name="dateOfBirth"
                              id="dateOfBirth"
                              maxLength={10}
                              placeholder="DD/MM/YYYY"
                              value={values.dateOfBirth}
                              onChange={handleChange}
                              onBlur={() => {
                                if (values.dateOfBirth) {
                                  const normalized = moment(values.dateOfBirth, "DD/MM/YYYY", true);
                                  if (normalized.isValid()) {
                                    setFieldValue("dateOfBirth", normalized.format("DD/MM/YYYY"));
                                  }
                                }
                              }}
                              style={{ width: "100%", paddingRight: "50px" }}
                            />
                            {props.errors.dateOfBirth && props.touched.dateOfBirth && (
                              <div style={{ color: "red", fontSize: "12px" }}>{props.errors.dateOfBirth}</div>
                            )}
                            <EditContainer>
                              <Edit
                                onClick={() => handleSave("dateOfBirth", values.dateOfBirth, setFieldValue, values)}
                              >
                                <SmallButtonIconContainer
                                  color={styleTheme.palette.vodafoneRed}
                                  style={{ marginRight: 5 }}
                                >
                                  <CheckIcon />
                                </SmallButtonIconContainer>
                              </Edit>
                              <Edit onClick={() => handleCancel("dateOfBirth")}>
                                <SmallButtonIconContainer
                                  color={styleTheme.palette.vodafoneRed}
                                  style={{ marginRight: 5 }}
                                >
                                  <CancelIcon />
                                </SmallButtonIconContainer>
                              </Edit>
                            </EditContainer>
                          </EditableWrapper>
                        ) : (
                          <NoEditableWrapper>
                            <div>
                              <NonEditableText>
                                {moment(values.dateOfBirth, "DD/MM/YYYY", true).isValid()
                                  ? renderDate({
                                      date: moment(values.dateOfBirth, "DD/MM/YYYY", true),
                                      printHour: false,
                                    })
                                  : values.dateOfBirth}
                              </NonEditableText>
                            </div>
                            {isEditableCheck && (
                              <Edit onClick={() => handleEditClick("dateOfBirth", values.dateOfBirth)}>
                                <div style={{ position: "relative", left: "30px" }}>
                                  <SmallButtonIconContainer
                                    color={styleTheme.palette.vodafoneRed}
                                    style={{ marginRight: 5 }}
                                  >
                                    <EditIcon />
                                  </SmallButtonIconContainer>
                                </div>
                              </Edit>
                            )}
                          </NoEditableWrapper>
                        )}

                        {detailsData.dateOfBirth?.status === "REVIEW" && (
                          <MessageWrapper>
                            <ManualEntryLabel>{t("pages.docSubmissionDetails.manualReview")}</ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.turquoiseBlue} />
                          </MessageWrapper>
                        )}

                        {detailsData.dateOfBirth?.status === "EDITED" && (
                          <MessageWrapper>
                            <ManualEntryLabel color={styleTheme.palette.warningYellow}>
                              {t("pages.docSubmissionDetails.updatedFields")}
                            </ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.warningYellow} />
                          </MessageWrapper>
                        )}
                      </ManualEntryWrapper>
                    ),
                  },
                  {
                    label: t("pages.docSubmissionDetails.placeOfBirth"),
                    displayComponent: detailsData.placeOfBirth?.value,
                    editingComponent: (
                      <ManualEntryWrapper>
                        {editingField === "placeOfBirth" || props.errors.placeOfBirth ? (
                          <EditableWrapper style={{ position: "relative", paddingBottom: "20px" }}>
                            <TextInput
                              name="placeOfBirth"
                              id={"placeOfBirth"}
                              maxLength={50}
                              onChange={e => handleChange(e)}
                              onBlur={props.handleBlur}
                              value={values.placeOfBirth}
                              style={{ width: "100%", paddingRight: "50px" }}
                            />
                            {props.errors.placeOfBirth && props.touched.placeOfBirth && (
                              <ValidationErrorFloating>{props.errors.placeOfBirth}</ValidationErrorFloating>
                            )}
                            <EditContainer style={{ position: "absolute", top: "30%" }}>
                              <Edit
                                onClick={async () => {
                                  props.setFieldTouched("placeOfBirth", true);
                                  const errors = await props.validateForm();
                                  if (!errors.placeOfBirth) {
                                    handleSave("placeOfBirth", values.placeOfBirth, setFieldValue, values);
                                  }
                                }}
                              >
                                <SmallButtonIconContainer
                                  color={styleTheme.palette.vodafoneRed}
                                  style={{ marginRight: 5 }}
                                >
                                  <CheckIcon />
                                </SmallButtonIconContainer>
                              </Edit>

                              <Edit onClick={() => handleCancel("placeOfBirth")}>
                                <SmallButtonIconContainer
                                  color={styleTheme.palette.vodafoneRed}
                                  style={{ marginRight: 5 }}
                                >
                                  <CancelIcon />
                                </SmallButtonIconContainer>
                              </Edit>
                            </EditContainer>
                          </EditableWrapper>
                        ) : (
                          <NoEditableWrapper>
                            <div>
                              <NonEditableText>{values.placeOfBirth}</NonEditableText>
                            </div>
                            {isEditableCheck && (
                              <div>
                                <Edit
                                  onClick={() => {
                                    handleEditClick("placeOfBirth", values.placeOfBirth);
                                  }}
                                >
                                  <div style={{ position: "relative", left: "30px" }}>
                                    <SmallButtonIconContainer
                                      color={styleTheme.palette.vodafoneRed}
                                      style={{ marginRight: 5 }}
                                    >
                                      <EditIcon />
                                    </SmallButtonIconContainer>
                                  </div>
                                </Edit>
                              </div>
                            )}
                          </NoEditableWrapper>
                        )}

                        {detailsData.placeOfBirth?.status === "REVIEW" && (
                          <MessageWrapper>
                            <ManualEntryLabel>{t("pages.docSubmissionDetails.manualReview")}</ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.turquoiseBlue} />
                          </MessageWrapper>
                        )}
                        {detailsData.placeOfBirth?.status === "EDITED" && (
                          <MessageWrapper>
                            <ManualEntryLabel color={styleTheme.palette.warningYellow}>
                              {t("pages.docSubmissionDetails.updatedFields")}
                            </ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.warningYellow} />
                          </MessageWrapper>
                        )}
                      </ManualEntryWrapper>
                    ),
                  },
                  {
                    label: t("pages.docSubmissionDetails.gender"),
                    displayComponent: tMarket(`genders.${detailsData.gender?.value}`),
                    editingComponent: (
                      <ManualEntryWrapper>
                        {isEditableCheck && editMode && editingField === "gender" ? (
                          <EditableWrapper>
                            <AutoClosingDropdown
                              options={[
                                { key: "0", label: tMarket("genders.0") },
                                { key: "1", label: tMarket("genders.1") },
                              ]}
                              dropdownType={DropdownType.RECTANGULAR_NORMAL}
                              selectOption={opt => {
                                setFieldValue("gender", opt.key);
                              }}
                              label={
                                tMarket(`genders.${String(values.gender)}`) ||
                                tMarket(`genders.${detailsData.gender?.value}`) ||
                                "Select Gender"
                              }
                              hasValue={Boolean(values.gender || detailsData.gender?.value)}
                            />
                          </EditableWrapper>
                        ) : (
                          <NoEditableWrapper>
                            <div>
                              <NonEditableText>{tMarket(`genders.${detailsData.gender?.value}`)}</NonEditableText>
                            </div>
                            {isEditableCheck && (
                              <div>
                                <Edit onClick={() => handleEditClick("gender", values.gender)}>
                                  <div style={{ position: "relative", left: "30px" }}>
                                    <SmallButtonIconContainer
                                      color={styleTheme.palette.vodafoneRed}
                                      style={{ marginRight: 5 }}
                                    >
                                      <EditIcon />
                                    </SmallButtonIconContainer>
                                  </div>
                                </Edit>
                              </div>
                            )}
                          </NoEditableWrapper>
                        )}

                        {detailsData.gender?.status === "REVIEW" && (
                          <MessageWrapper>
                            <ManualEntryLabel>{t("pages.docSubmissionDetails.manualReview")}</ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.turquoiseBlue} />
                          </MessageWrapper>
                        )}

                        {detailsData.gender?.status === "EDITED" && (
                          <MessageWrapper>
                            <ManualEntryLabel color={styleTheme.palette.warningYellow}>
                              {t("pages.docSubmissionDetails.updatedFields")}
                            </ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.warningYellow} />
                          </MessageWrapper>
                        )}
                      </ManualEntryWrapper>
                    ),
                  },
                  {
                    label: t("pages.docSubmissionDetails.idType"),
                    displayComponent:
                      values.idType || getDocumentLabel(values.documentType || detailsData.documentType),
                    editingComponent: (
                      <ManualEntryWrapper>
                        {detailsData.whiteListedUser && isEditableCheck && editMode && editingField === "idType" ? (
                          <EditableWrapper style={{ position: "relative" }}>
                            <AutoClosingDropdown
                              options={identities.map(doc => ({
                                key: doc.documentId,
                                label: isFrench ? doc.fr : doc.en,
                              }))}
                              dropdownType={DropdownType.RECTANGULAR_NORMAL}
                              selectOption={opt => {
                                setFieldValue("idType", opt.label);
                                setFieldValue("idTypeKey", opt.key);
                                setFieldValue("documentType", opt.key);
                                handleEditClick("idType", opt.label);
                              }}
                              label={
                                values.idType ||
                                (isFrench
                                  ? identities.find(
                                      d => d.documentId === (values.documentType || detailsData.documentType),
                                    )?.fr
                                  : identities.find(
                                      d => d.documentId === (values.documentType || detailsData.documentType),
                                    )?.en)
                              }
                              hasValue={Boolean(values.idType || detailsData.documentType)}
                            />
                          </EditableWrapper>
                        ) : (
                          <NoEditableWrapper>
                            <div>
                              <NonEditableText>
                                {values.idType || getDocumentLabel(values.documentType || detailsData.documentType)}
                              </NonEditableText>
                            </div>
                            {detailsData.whiteListedUser && isEditableCheck && (
                              <div>
                                <Edit onClick={() => handleEditClick("idType", values.idType)}>
                                  <div style={{ position: "relative", left: "30px" }}>
                                    <SmallButtonIconContainer
                                      color={styleTheme.palette.vodafoneRed}
                                      style={{ marginRight: 5 }}
                                    >
                                      <EditIcon />
                                    </SmallButtonIconContainer>
                                  </div>
                                </Edit>
                              </div>
                            )}
                          </NoEditableWrapper>
                        )}

                        {detailsData.documentType === "REVIEW" && (
                          <MessageWrapper>
                            <ManualEntryLabel>{t("pages.docSubmissionDetails.manualReview")}</ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.turquoiseBlue} />
                          </MessageWrapper>
                        )}

                        {detailsData.documentType === "EDITED" && (
                          <MessageWrapper>
                            <ManualEntryLabel color={styleTheme.palette.warningYellow}>
                              {t("pages.docSubmissionDetails.updatedFields")}
                            </ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.warningYellow} />
                          </MessageWrapper>
                        )}
                      </ManualEntryWrapper>
                    ),
                  },
                  {
                    label: t("pages.docSubmissionDetails.idNumber"),
                    displayComponent: detailsData.idNumber?.value,
                    editingComponent: (
                      <ManualEntryWrapper>
                        {editingField === "idNumber" || props.errors.idNumber ? (
                          <EditableWrapper style={{ position: "relative", paddingBottom: "20px" }}>
                            <TextInput
                              name="idNumber"
                              id={"idNumber"}
                              maxLength={12}
                              onChange={e => handleChange(e)}
                              onBlur={props.handleBlur}
                              value={values.idNumber}
                              style={{ width: "100%", paddingRight: "50px" }}
                            />
                            {props.errors.idNumber && props.touched.idNumber && (
                              <ValidationErrorFloating>{props.errors.idNumber}</ValidationErrorFloating>
                            )}
                            <EditContainer style={{ position: "absolute", top: "30%" }}>
                              <Edit
                                onClick={async () => {
                                  props.setFieldTouched("idNumber", true);
                                  const errors = await props.validateForm();
                                  if (!errors.idNumber) {
                                    handleSave("idNumber", values.idNumber, setFieldValue, values);
                                  }
                                }}
                              >
                                <SmallButtonIconContainer
                                  color={styleTheme.palette.vodafoneRed}
                                  style={{ marginRight: 5 }}
                                >
                                  <CheckIcon />
                                </SmallButtonIconContainer>
                              </Edit>

                              <Edit onClick={() => handleCancel("idNumber")}>
                                <SmallButtonIconContainer
                                  color={styleTheme.palette.vodafoneRed}
                                  style={{ marginRight: 5 }}
                                >
                                  <CancelIcon />
                                </SmallButtonIconContainer>
                              </Edit>
                            </EditContainer>
                          </EditableWrapper>
                        ) : (
                          <NoEditableWrapper>
                            <div>
                              <NonEditableText>{values.idNumber}</NonEditableText>
                            </div>
                            {isEditableCheck && (
                              <div>
                                <Edit
                                  onClick={() => {
                                    handleEditClick("idNumber", values.idNumber);
                                  }}
                                >
                                  <div style={{ position: "relative", left: "30px" }}>
                                    <SmallButtonIconContainer
                                      color={styleTheme.palette.vodafoneRed}
                                      style={{ marginRight: 5 }}
                                    >
                                      <EditIcon />
                                    </SmallButtonIconContainer>
                                  </div>
                                </Edit>
                              </div>
                            )}
                          </NoEditableWrapper>
                        )}

                        {detailsData.idNumber?.status === "REVIEW" && (
                          <MessageWrapper>
                            <ManualEntryLabel>{t("pages.docSubmissionDetails.manualReview")}</ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.turquoiseBlue} />
                          </MessageWrapper>
                        )}
                        {detailsData.idNumber?.status === "EDITED" && (
                          <MessageWrapper>
                            <ManualEntryLabel color={styleTheme.palette.warningYellow}>
                              {t("pages.docSubmissionDetails.updatedFields")}
                            </ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.warningYellow} />
                          </MessageWrapper>
                        )}
                      </ManualEntryWrapper>
                    ),
                  },
                ];

                rows.push.apply(rows, [
                  {
                    label: t("pages.docSubmissionDetails.nationality"),
                    displayComponent: values.nationality || detailsData.nationality?.value,
                    editingComponent: (
                      <ManualEntryWrapper>
                        {detailsData.whiteListedUser &&
                        isEditableCheck &&
                        editMode &&
                        editingField === "nationality" ? (
                          <EditableWrapper style={{ position: "relative" }}>
                            <TextInput
                              placeholder="Search nationality..."
                              value={searchNationality}
                              onFocus={() => setIsFocused(true)}
                              onBlur={() => setTimeout(() => setIsFocused(false), 150)}
                              onChange={e => {
                                const input = e.target.value;
                                setSearchNationality(input);
                                setFieldValue("nationality", input === "" ? "" : values.nationality);
                                setFieldValue("nationalityKey", "");
                              }}
                              style={{ width: "100%", marginBottom: "10px" }}
                            />

                            {isFocused && (
                              <DropdownContainer>
                                {filteredNationalities.length > 0 ? (
                                  filteredNationalities.map(nat => (
                                    <DropdownItem
                                      key={nat.key}
                                      selected={values.nationality === nat.description}
                                      onClick={() => {
                                        const selectedNationality = NATIONALITIES.find(n => n.key === nat.key);
                                        const desc = selectedNationality?.description || "";

                                        setFieldValue("nationality", desc);
                                        setFieldValue("nationalityKey", nat.key);
                                        setSearchNationality(desc);
                                        setIsFocused(false);
                                        setTimeout(() => {
                                          handleEditClick("nationality", desc);
                                        }, 0);
                                      }}
                                    >
                                      {nat.description}
                                    </DropdownItem>
                                  ))
                                ) : (
                                  <DropdownItem disabled>No results found</DropdownItem>
                                )}
                              </DropdownContainer>
                            )}
                          </EditableWrapper>
                        ) : (
                          <NoEditableWrapper>
                            <div>
                              <NonEditableText>{detailsData.nationality?.value}</NonEditableText>
                            </div>
                            {detailsData.whiteListedUser && isEditableCheck && (
                              <div>
                                <Edit onClick={() => handleEditClick("nationality", values.nationality)}>
                                  <div style={{ position: "relative", left: "30px" }}>
                                    <SmallButtonIconContainer
                                      color={styleTheme.palette.vodafoneRed}
                                      style={{ marginRight: 5 }}
                                    >
                                      <EditIcon />
                                    </SmallButtonIconContainer>
                                  </div>
                                </Edit>
                              </div>
                            )}
                          </NoEditableWrapper>
                        )}

                        {detailsData.nationality?.status === "REVIEW" && (
                          <MessageWrapper>
                            <ManualEntryLabel>{t("pages.docSubmissionDetails.manualReview")}</ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.turquoiseBlue} />
                          </MessageWrapper>
                        )}

                        {detailsData.nationality?.status === "EDITED" && (
                          <MessageWrapper>
                            <ManualEntryLabel color={styleTheme.palette.warningYellow}>
                              {t("pages.docSubmissionDetails.updatedFields")}
                            </ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.warningYellow} />
                          </MessageWrapper>
                        )}
                      </ManualEntryWrapper>
                    ),
                  },
                  {
                    label: t("pages.docSubmissionDetails.addressStreet"),
                    displayComponent: detailsData.addressStreet?.value,
                    editingComponent: (
                      <ManualEntryWrapper>
                        {editingField === "addressStreet" || props.errors.addressStreet ? (
                          <EditableWrapper style={{ position: "relative", paddingBottom: "20px" }}>
                            <TextInput
                              name="addressStreet"
                              id={"address-street"}
                              maxLength={50}
                              onChange={e => handleChange(e)}
                              onBlur={props.handleBlur}
                              value={values.addressStreet}
                              style={{ width: "100%", paddingRight: "50px" }}
                            />
                            {props.errors.addressStreet && props.touched.addressStreet && (
                              <ValidationErrorFloating>{props.errors.addressStreet}</ValidationErrorFloating>
                            )}
                            <EditContainer style={{ position: "absolute", top: "30%" }}>
                              <Edit
                                onClick={async () => {
                                  props.setFieldTouched("addressStreet", true);
                                  const errors = await props.validateForm();
                                  if (!errors.addressStreet) {
                                    handleSave("addressStreet", values.addressStreet, setFieldValue, values);
                                  }
                                }}
                              >
                                <SmallButtonIconContainer
                                  color={styleTheme.palette.vodafoneRed}
                                  style={{ marginRight: 5 }}
                                >
                                  <CheckIcon />
                                </SmallButtonIconContainer>
                              </Edit>

                              <Edit onClick={() => handleCancel("addressStreet")}>
                                <SmallButtonIconContainer
                                  color={styleTheme.palette.vodafoneRed}
                                  style={{ marginRight: 5 }}
                                >
                                  <CancelIcon />
                                </SmallButtonIconContainer>
                              </Edit>
                            </EditContainer>
                          </EditableWrapper>
                        ) : (
                          <NoEditableWrapper>
                            <div>
                              <NonEditableText>{values.addressStreet}</NonEditableText>
                            </div>
                            {isEditableCheck && (
                              <div>
                                <Edit
                                  onClick={() => {
                                    handleEditClick("addressStreet", values.addressStreet);
                                  }}
                                >
                                  <div style={{ position: "relative", left: "30px" }}>
                                    <SmallButtonIconContainer
                                      color={styleTheme.palette.vodafoneRed}
                                      style={{ marginRight: 5 }}
                                    >
                                      <EditIcon />
                                    </SmallButtonIconContainer>
                                  </div>
                                </Edit>
                              </div>
                            )}
                          </NoEditableWrapper>
                        )}

                        {detailsData.addressStreet?.status === "REVIEW" && (
                          <MessageWrapper>
                            <ManualEntryLabel>{t("pages.docSubmissionDetails.manualReview")}</ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.turquoiseBlue} />
                          </MessageWrapper>
                        )}
                        {detailsData.addressStreet?.status === "EDITED" && (
                          <MessageWrapper>
                            <ManualEntryLabel color={styleTheme.palette.warningYellow}>
                              {t("pages.docSubmissionDetails.updatedFields")}
                            </ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.warningYellow} />
                          </MessageWrapper>
                        )}
                      </ManualEntryWrapper>
                    ),
                  },
                  {
                    label: t("pages.docSubmissionDetails.addressProvince"),
                    displayComponent: detailsData.addressProvince?.value,
                    editingComponent: (
                      <ManualEntryWrapper>
                        {isEditableCheck && editMode && editingField === "addressProvince" ? (
                          <EditableWrapper>
                            <AutoClosingDropdown
                              options={PROVINCES.map(province => ({
                                label: province.description,
                                key: province.key,
                              }))}
                              dropdownType={DropdownType.RECTANGULAR_NORMAL}
                              selectOption={opt => {
                                const provinceID = Number(opt.key.replace("province.", ""));
                                const provinceName = PROVINCES.find(p => p.id === provinceID)?.description || "";
                                setFieldValue("addressProvince", provinceName);
                                setFieldValue("provinceID", provinceID);
                                setFieldValue("addressTown", "");
                              }}
                              label={
                                values.addressProvince || detailsData.addressProvince?.value || "Select a Province"
                              }
                              hasValue={Boolean(values.addressProvince || detailsData.addressProvince?.value)}
                            />
                          </EditableWrapper>
                        ) : (
                          <NoEditableWrapper>
                            <div>
                              <NonEditableText>
                                {PROVINCES.find(p => p.key === values.addressProvince)?.description ||
                                  values.addressProvince}
                              </NonEditableText>
                            </div>
                            {isEditableCheck && (
                              <div>
                                <Edit
                                  onClick={() => {
                                    handleEditClick("addressProvince", values.addressProvince);
                                  }}
                                >
                                  <div style={{ position: "relative", left: "30px" }}>
                                    <SmallButtonIconContainer
                                      color={styleTheme.palette.vodafoneRed}
                                      style={{ marginRight: 5 }}
                                    >
                                      <EditIcon />
                                    </SmallButtonIconContainer>
                                  </div>
                                </Edit>
                              </div>
                            )}
                          </NoEditableWrapper>
                        )}

                        {detailsData.addressProvince?.status === "REVIEW" && (
                          <MessageWrapper>
                            <ManualEntryLabel>{t("pages.docSubmissionDetails.manualReview")}</ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.turquoiseBlue} />
                          </MessageWrapper>
                        )}
                        {detailsData.addressProvince?.status === "EDITED" && (
                          <MessageWrapper>
                            <ManualEntryLabel color={styleTheme.palette.warningYellow}>
                              {t("pages.docSubmissionDetails.updatedFields")}
                            </ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.warningYellow} />
                          </MessageWrapper>
                        )}
                      </ManualEntryWrapper>
                    ),
                  },
                  {
                    label: t("pages.docSubmissionDetails.addressTown"),
                    displayComponent: detailsData.addressTown?.value,
                    editingComponent: (
                      <ManualEntryWrapper>
                        {isEditableCheck && editMode && editingField === "addressTown" ? (
                          <EditableWrapper>
                            <AutoClosingDropdown
                              options={townsDescription
                                .filter(town => town.provinceID === values.provinceID)
                                .map(town => ({
                                  label: town.description,
                                  key: town.id,
                                }))}
                              dropdownType={DropdownType.RECTANGULAR_NORMAL}
                              selectOption={opt => {
                                setFieldValue("addressTown", opt.key);
                                setFieldValue("addressTown", opt.label);
                              }}
                              label={values.addressTown || detailsData.addressTown?.value || "Select a Town"}
                              hasValue={!!values.addressTown}
                            />
                          </EditableWrapper>
                        ) : (
                          <NoEditableWrapper>
                            <div>
                              <NonEditableText>
                                {values.addressTown
                                  ? townsDescription.find(t => t.id === values.addressTown)?.description ||
                                    values.addressTown
                                  : "Select a town"}
                              </NonEditableText>
                            </div>
                            {isEditableCheck && (
                              <div>
                                <Edit
                                  onClick={() => {
                                    handleEditClick("addressTown", values.addressTown);
                                  }}
                                >
                                  <div style={{ position: "relative", left: "30px" }}>
                                    <SmallButtonIconContainer
                                      color={styleTheme.palette.vodafoneRed}
                                      style={{ marginRight: 5 }}
                                    >
                                      <EditIcon />
                                    </SmallButtonIconContainer>
                                  </div>
                                </Edit>
                              </div>
                            )}
                          </NoEditableWrapper>
                        )}

                        {detailsData.addressTown?.status === "REVIEW" && (
                          <MessageWrapper>
                            <ManualEntryLabel>{t("pages.docSubmissionDetails.manualReview")}</ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.turquoiseBlue} />
                          </MessageWrapper>
                        )}
                        {detailsData.addressTown?.status === "EDITED" && (
                          <MessageWrapper>
                            <ManualEntryLabel color={styleTheme.palette.warningYellow}>
                              {t("pages.docSubmissionDetails.updatedFields")}
                            </ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.warningYellow} />
                          </MessageWrapper>
                        )}
                      </ManualEntryWrapper>
                    ),
                  },
                  {
                    label: t("pages.docSubmissionDetails.addressCommune"),
                    displayComponent: detailsData.addressCommune?.value,
                    editingComponent: (
                      <ManualEntryWrapper>
                        {editingField === "addressCommune" || props.errors.addressCommune ? (
                          <EditableWrapper style={{ position: "relative", paddingBottom: "20px" }}>
                            <TextInput
                              name="addressCommune"
                              id={"address-commune"}
                              maxLength={50}
                              onChange={e => handleChange(e)}
                              onBlur={props.handleBlur}
                              value={values.addressCommune}
                              style={{ width: "100%", paddingRight: "50px" }}
                            />
                            {props.errors.addressCommune && props.touched.addressCommune && (
                              <ValidationErrorFloating>{props.errors.addressCommune}</ValidationErrorFloating>
                            )}
                            <EditContainer style={{ position: "absolute", top: "30%" }}>
                              <Edit
                                onClick={async () => {
                                  props.setFieldTouched("addressCommune", true);
                                  const errors = await props.validateForm();
                                  if (!errors.addressCommune) {
                                    handleSave("addressCommune", values.addressCommune, setFieldValue, values);
                                  }
                                }}
                              >
                                <SmallButtonIconContainer
                                  color={styleTheme.palette.vodafoneRed}
                                  style={{ marginRight: 5 }}
                                >
                                  <CheckIcon />
                                </SmallButtonIconContainer>
                              </Edit>

                              <Edit onClick={() => handleCancel("addressCommune")}>
                                <SmallButtonIconContainer
                                  color={styleTheme.palette.vodafoneRed}
                                  style={{ marginRight: 5 }}
                                >
                                  <CancelIcon />
                                </SmallButtonIconContainer>
                              </Edit>
                            </EditContainer>
                          </EditableWrapper>
                        ) : (
                          <NoEditableWrapper>
                            <div>
                              <NonEditableText>{values.addressCommune}</NonEditableText>
                            </div>
                            {isEditableCheck && (
                              <div>
                                <Edit
                                  onClick={() => {
                                    handleEditClick("addressCommune", values.addressCommune);
                                  }}
                                >
                                  <div style={{ position: "relative", left: "30px" }}>
                                    <SmallButtonIconContainer
                                      color={styleTheme.palette.vodafoneRed}
                                      style={{ marginRight: 5 }}
                                    >
                                      <EditIcon />
                                    </SmallButtonIconContainer>
                                  </div>
                                </Edit>
                              </div>
                            )}
                          </NoEditableWrapper>
                        )}

                        {detailsData.addressCommune?.status === "REVIEW" && (
                          <MessageWrapper>
                            <ManualEntryLabel>{t("pages.docSubmissionDetails.manualReview")}</ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.turquoiseBlue} />
                          </MessageWrapper>
                        )}
                        {detailsData.addressCommune?.status === "EDITED" && (
                          <MessageWrapper>
                            <ManualEntryLabel color={styleTheme.palette.warningYellow}>
                              {t("pages.docSubmissionDetails.updatedFields")}
                            </ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.warningYellow} />
                          </MessageWrapper>
                        )}
                      </ManualEntryWrapper>
                    ),
                  },
                  {
                    label: t("pages.docSubmissionDetails.occupation"),
                    displayComponent: (() => {
                      const key = values.occupationKey || detailsData.occupation?.value;
                      const selected = OCCUPATIONS_MAP[key as string];
                      return selected ? (i18n.language === "fr" ? selected.FR : selected.EN) : "";
                    })(),
                    editingComponent: (
                      <ManualEntryWrapper>
                        {isEditableCheck && editMode && editingField === "occupation" ? (
                          <EditableWrapper style={{ position: "relative" }}>
                            {/* Search Input */}
                            <TextInput
                              placeholder="Search occupation..."
                              value={searchOccupation}
                              onFocus={() => setIsOccupationFocused(true)}
                              onBlur={() => setTimeout(() => setIsOccupationFocused(false), 150)}
                              onChange={e => {
                                const input = e.target.value;
                                setSearchOccupation(input);
                                setFieldValue("occupationKey", "");
                                setFieldValue("occupation", input === "" ? "" : values.occupation);
                              }}
                              style={{ width: "100%", marginBottom: "10px" }}
                            />

                            {/* Dropdown list */}
                            {isOccupationFocused && (
                              <DropdownContainer>
                                {filteredOccupations.length > 0 ? (
                                  filteredOccupations.map(occ => (
                                    <DropdownItem
                                      key={occ.key}
                                      selected={values.occupationKey === occ.key}
                                      onClick={() => {
                                        const selected = OCCUPATIONS_MAP[String(occ.key)];
                                        if (selected) {
                                          setFieldValue("occupationKey", selected.KEY);
                                          setFieldValue("occupation", selected.EN);
                                          setSearchOccupation(i18n.language === "fr" ? selected.FR : selected.EN);
                                          setIsOccupationFocused(false);
                                          setTimeout(() => {
                                            handleEditClick("occupation", selected.EN);
                                          }, 0);
                                        }
                                      }}
                                    >
                                      {i18n.language === "fr" ? occ.frLabel : occ.label}
                                    </DropdownItem>
                                  ))
                                ) : (
                                  <DropdownItem disabled>No results found</DropdownItem>
                                )}
                              </DropdownContainer>
                            )}
                          </EditableWrapper>
                        ) : (
                          <NoEditableWrapper>
                            <div>
                              <NonEditableText>
                                {(() => {
                                  const key = values.occupationKey || detailsData.occupation?.value;
                                  const selected = OCCUPATIONS_MAP[key as string];
                                  return selected ? (i18n.language === "fr" ? selected.FR : selected.EN) : "";
                                })()}
                              </NonEditableText>
                            </div>
                            {isEditableCheck && (
                              <div>
                                <Edit onClick={() => handleEditClick("occupation", values.occupation)}>
                                  <div style={{ position: "relative", left: "30px" }}>
                                    <SmallButtonIconContainer
                                      color={styleTheme.palette.vodafoneRed}
                                      style={{ marginRight: 5 }}
                                    >
                                      <EditIcon />
                                    </SmallButtonIconContainer>
                                  </div>
                                </Edit>
                              </div>
                            )}
                          </NoEditableWrapper>
                        )}

                        {/* REVIEW & EDITED status messages */}
                        {detailsData.occupation?.status === "REVIEW" && (
                          <MessageWrapper>
                            <ManualEntryLabel>{t("pages.docSubmissionDetails.manualReview")}</ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.turquoiseBlue} />
                          </MessageWrapper>
                        )}
                        {detailsData.occupation?.status === "EDITED" && (
                          <MessageWrapper>
                            <ManualEntryLabel color={styleTheme.palette.warningYellow}>
                              {t("pages.docSubmissionDetails.updatedFields")}
                            </ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.warningYellow} />
                          </MessageWrapper>
                        )}
                      </ManualEntryWrapper>
                    ),
                  },
                  {
                    label: t("pages.docSubmissionDetails.incomeRangeUsd"),
                    displayComponent: values.income || detailsData.income?.value,
                    editingComponent: (
                      <ManualEntryWrapper>
                        {isEditableCheck && editMode && editingField === "income" ? (
                          <EditableWrapper>
                            <AutoClosingDropdown
                              options={INCOME_RANGES.map(income => ({
                                label: income.description,
                                key: income.key,
                              }))}
                              dropdownType={DropdownType.RECTANGULAR_NORMAL}
                              selectOption={opt => {
                                const incomeName = INCOME_RANGES.find(i => i.key === opt.key)?.description || "";
                                setFieldValue("income", incomeName);
                                setFieldValue("incomeKey", opt.key);
                              }}
                              label={values.income || detailsData.income?.value || "Select an Income"}
                              hasValue={Boolean(values.income || detailsData.income?.value)}
                            />
                          </EditableWrapper>
                        ) : (
                          <NoEditableWrapper>
                            <div>
                              <NonEditableText>
                                {INCOME_RANGES.find(i => i.key === values.income)?.description || values.income}
                              </NonEditableText>
                            </div>
                            {isEditableCheck && (
                              <div>
                                <Edit onClick={() => handleEditClick("income", values.income)}>
                                  <div style={{ position: "relative", left: "30px" }}>
                                    <SmallButtonIconContainer
                                      color={styleTheme.palette.vodafoneRed}
                                      style={{ marginRight: 5 }}
                                    >
                                      <EditIcon />
                                    </SmallButtonIconContainer>
                                  </div>
                                </Edit>
                              </div>
                            )}
                          </NoEditableWrapper>
                        )}

                        {detailsData.income?.status === "REVIEW" && (
                          <MessageWrapper>
                            <ManualEntryLabel>{t("pages.docSubmissionDetails.manualReview")}</ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.turquoiseBlue} />
                          </MessageWrapper>
                        )}
                        {detailsData.income?.status === "EDITED" && (
                          <MessageWrapper>
                            <ManualEntryLabel color={styleTheme.palette.warningYellow}>
                              {t("pages.docSubmissionDetails.updatedFields")}
                            </ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.warningYellow} />
                          </MessageWrapper>
                        )}
                      </ManualEntryWrapper>
                    ),
                  },

                  {
                    label: t("pages.docSubmissionDetails.riskRating"),
                    displayComponent: getRiskDescription(values.riskRating || detailsData.riskRating?.value),
                    editingComponent: (
                      <ManualEntryWrapper>
                        {isEditableCheck && editMode && editingField === "riskRating" ? (
                          <EditableWrapper>
                            <AutoClosingDropdown
                              options={RISK_RATINGS.map(risk => ({
                                label: risk.description,
                                key: String(risk.id),
                              }))}
                              dropdownType={DropdownType.RECTANGULAR_NORMAL}
                              selectOption={opt => {
                                setFieldValue("riskRating", opt.key);
                              }}
                              label={
                                getRiskDescription(values.riskRating || detailsData.riskRating?.value) ||
                                "Select a Risk Rating"
                              }
                              hasValue={Boolean(values.riskRating || detailsData.riskRating?.value)}
                            />
                          </EditableWrapper>
                        ) : (
                          <NoEditableWrapper>
                            <div>
                              <NonEditableText>
                                {getRiskDescription(values.riskRating || detailsData.riskRating?.value)}
                              </NonEditableText>
                            </div>
                            {isEditableCheck && (
                              <div>
                                <Edit onClick={() => handleEditClick("riskRating", values.riskRating)}>
                                  <div style={{ position: "relative", left: "30px" }}>
                                    <SmallButtonIconContainer
                                      color={styleTheme.palette.vodafoneRed}
                                      style={{ marginRight: 5 }}
                                    >
                                      <EditIcon />
                                    </SmallButtonIconContainer>
                                  </div>
                                </Edit>
                              </div>
                            )}
                          </NoEditableWrapper>
                        )}

                        {detailsData.riskRating?.status === "REVIEW" && (
                          <MessageWrapper>
                            <ManualEntryLabel>{t("pages.docSubmissionDetails.manualReview")}</ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.turquoiseBlue} />
                          </MessageWrapper>
                        )}
                        {detailsData.riskRating?.status === "EDITED" && (
                          <MessageWrapper>
                            <ManualEntryLabel color={styleTheme.palette.warningYellow}>
                              {t("pages.docSubmissionDetails.updatedFields")}
                            </ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.warningYellow} />
                          </MessageWrapper>
                        )}
                      </ManualEntryWrapper>
                    ),
                  },

                  {
                    label: t("pages.docSubmissionDetails.idCardIssuingBy"),
                    displayComponent: detailsData.idCardIssuingBy?.value,
                    editingComponent: (
                      <ManualEntryWrapper>
                        {editingField === "idCardIssuingBy" || props.errors.idCardIssuingBy ? (
                          <EditableWrapper style={{ position: "relative", paddingBottom: "20px" }}>
                            <TextInput
                              name="idCardIssuingBy"
                              id={"idCardIssuingBy"}
                              maxLength={50}
                              onChange={e => handleChange(e)}
                              onBlur={props.handleBlur}
                              value={values.idCardIssuingBy}
                              style={{ width: "100%", paddingRight: "50px" }}
                            />
                            {props.errors.idCardIssuingBy && props.touched.idCardIssuingBy && (
                              <ValidationErrorFloating>{props.errors.idCardIssuingBy}</ValidationErrorFloating>
                            )}
                            <EditContainer style={{ position: "absolute", top: "30%" }}>
                              <Edit
                                onClick={async () => {
                                  props.setFieldTouched("idCardIssuingBy", true);
                                  const errors = await props.validateForm();
                                  if (!errors.idCardIssuingBy) {
                                    handleSave("idCardIssuingBy", values.idCardIssuingBy, setFieldValue, values);
                                  }
                                }}
                              >
                                <SmallButtonIconContainer
                                  color={styleTheme.palette.vodafoneRed}
                                  style={{ marginRight: 5 }}
                                >
                                  <CheckIcon />
                                </SmallButtonIconContainer>
                              </Edit>

                              <Edit onClick={() => handleCancel("idCardIssuingBy")}>
                                <SmallButtonIconContainer
                                  color={styleTheme.palette.vodafoneRed}
                                  style={{ marginRight: 5 }}
                                >
                                  <CancelIcon />
                                </SmallButtonIconContainer>
                              </Edit>
                            </EditContainer>
                          </EditableWrapper>
                        ) : (
                          <NoEditableWrapper>
                            <div>
                              <NonEditableText>{values.idCardIssuingBy}</NonEditableText>
                            </div>
                            {isEditableCheck && (
                              <div>
                                <Edit
                                  onClick={() => {
                                    handleEditClick("idCardIssuingBy", values.idCardIssuingBy);
                                  }}
                                >
                                  <div style={{ position: "relative", left: "30px" }}>
                                    <SmallButtonIconContainer
                                      color={styleTheme.palette.vodafoneRed}
                                      style={{ marginRight: 5 }}
                                    >
                                      <EditIcon />
                                    </SmallButtonIconContainer>
                                  </div>
                                </Edit>
                              </div>
                            )}
                          </NoEditableWrapper>
                        )}

                        {detailsData.idCardIssuingBy?.status === "REVIEW" && (
                          <MessageWrapper>
                            <ManualEntryLabel>{t("pages.docSubmissionDetails.manualReview")}</ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.turquoiseBlue} />
                          </MessageWrapper>
                        )}
                        {detailsData.idCardIssuingBy?.status === "EDITED" && (
                          <MessageWrapper>
                            <ManualEntryLabel color={styleTheme.palette.warningYellow}>
                              {t("pages.docSubmissionDetails.updatedFields")}
                            </ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.warningYellow} />
                          </MessageWrapper>
                        )}
                      </ManualEntryWrapper>
                    ),
                  },

                  {
                    label: t("pages.docSubmissionDetails.idCardIssuingDate"),
                    displayComponent: renderDate({
                      date: moment(detailsData.idCardIssuingDate?.value),
                      printHour: false,
                    }),
                    editingComponent: (
                      <ManualEntryWrapper>
                        {isEditableCheck && editMode && editingField === "idCardIssuingDate" ? (
                          <EditableWrapper>
                            <TextInput
                              name="idCardIssuingDate"
                              id="idCardIssuingDate"
                              maxLength={10}
                              placeholder="DD/MM/YYYY"
                              value={values.idCardIssuingDate}
                              onChange={handleChange}
                              style={{ width: "100%", paddingRight: "50px" }}
                            />
                            {props.errors.idCardIssuingDate && props.touched.idCardIssuingDate && (
                              <div style={{ color: "red", fontSize: "12px" }}>{props.errors.idCardIssuingDate}</div>
                            )}
                            <EditContainer>
                              <Edit
                                onClick={() =>
                                  handleSave("idCardIssuingDate", values.idCardIssuingDate, setFieldValue, values)
                                }
                              >
                                <SmallButtonIconContainer
                                  color={styleTheme.palette.vodafoneRed}
                                  style={{ marginRight: 5 }}
                                >
                                  <CheckIcon />
                                </SmallButtonIconContainer>
                              </Edit>
                              <Edit onClick={() => handleCancel("idCardIssuingDate")}>
                                <SmallButtonIconContainer
                                  color={styleTheme.palette.vodafoneRed}
                                  style={{ marginRight: 5 }}
                                >
                                  <CancelIcon />
                                </SmallButtonIconContainer>
                              </Edit>
                            </EditContainer>
                          </EditableWrapper>
                        ) : (
                          <NoEditableWrapper>
                            <div>
                              <NonEditableText>
                                {moment(values.idCardIssuingDate, "DD/MM/YYYY", true).isValid()
                                  ? renderDate({
                                      date: moment(values.idCardIssuingDate, "DD/MM/YYYY", true),
                                      printHour: false,
                                    })
                                  : values.idCardIssuingDate}
                              </NonEditableText>
                            </div>
                            {isEditableCheck && (
                              <Edit onClick={() => handleEditClick("idCardIssuingDate", values.idCardIssuingDate)}>
                                <div style={{ position: "relative", left: "30px" }}>
                                  <SmallButtonIconContainer
                                    color={styleTheme.palette.vodafoneRed}
                                    style={{ marginRight: 5 }}
                                  >
                                    <EditIcon />
                                  </SmallButtonIconContainer>
                                </div>
                              </Edit>
                            )}
                          </NoEditableWrapper>
                        )}

                        {detailsData.idCardIssuingDate?.status === "REVIEW" && (
                          <MessageWrapper>
                            <ManualEntryLabel>{t("pages.docSubmissionDetails.manualReview")}</ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.turquoiseBlue} />
                          </MessageWrapper>
                        )}

                        {detailsData.idCardIssuingDate?.status === "EDITED" && (
                          <MessageWrapper>
                            <ManualEntryLabel color={styleTheme.palette.warningYellow}>
                              {t("pages.docSubmissionDetails.updatedFields")}
                            </ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.warningYellow} />
                          </MessageWrapper>
                        )}
                      </ManualEntryWrapper>
                    ),
                  },
                  {
                    label: t("pages.docSubmissionDetails.idCardIssuingPlace"),
                    displayComponent: values.idCardIssuingPlace || detailsData.idCardIssuingPlace?.value,
                    editingComponent: (
                      <ManualEntryWrapper>
                        {isEditableCheck && editMode && editingField === "idCardIssuingPlace" ? (
                          <EditableWrapper>
                            <AutoClosingDropdown
                              options={IDCARDISSUEPLACE.map(place => ({
                                label: place.description,
                                key: place.key,
                              }))}
                              dropdownType={DropdownType.RECTANGULAR_NORMAL}
                              selectOption={opt => {
                                const placeName = IDCARDISSUEPLACE.find(p => p.key === opt.key)?.description || "";
                                setFieldValue("idCardIssuingPlace", placeName);
                                setFieldValue("idCardIssuingPlaceKey", opt.key);
                              }}
                              label={
                                values.idCardIssuingPlace ||
                                detailsData.idCardIssuingPlace?.value ||
                                "Select Issuing Place"
                              }
                              hasValue={Boolean(values.idCardIssuingPlace || detailsData.idCardIssuingPlace?.value)}
                            />
                          </EditableWrapper>
                        ) : (
                          <NoEditableWrapper>
                            <div>
                              <NonEditableText>
                                {IDCARDISSUEPLACE.find(p => p.key === values.idCardIssuingPlace)?.description ||
                                  values.idCardIssuingPlace}
                              </NonEditableText>
                            </div>
                            {isEditableCheck && (
                              <div>
                                <Edit onClick={() => handleEditClick("idCardIssuingPlace", values.idCardIssuingPlace)}>
                                  <div style={{ position: "relative", left: "30px" }}>
                                    <SmallButtonIconContainer
                                      color={styleTheme.palette.vodafoneRed}
                                      style={{ marginRight: 5 }}
                                    >
                                      <EditIcon />
                                    </SmallButtonIconContainer>
                                  </div>
                                </Edit>
                              </div>
                            )}
                          </NoEditableWrapper>
                        )}

                        {detailsData.idCardIssuingPlace?.status === "REVIEW" && (
                          <MessageWrapper>
                            <ManualEntryLabel>{t("pages.docSubmissionDetails.manualReview")}</ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.turquoiseBlue} />
                          </MessageWrapper>
                        )}
                        {detailsData.idCardIssuingPlace?.status === "EDITED" && (
                          <MessageWrapper>
                            <ManualEntryLabel color={styleTheme.palette.warningYellow}>
                              {t("pages.docSubmissionDetails.updatedFields")}
                            </ManualEntryLabel>
                            <WarningIcon id="warning-icon" stroke={styleTheme.palette.warningYellow} />
                          </MessageWrapper>
                        )}
                      </ManualEntryWrapper>
                    ),
                  },
                  {
                    label: t("pages.docSubmissionDetails.phoneNumber"),
                    displayComponent: detailsData.phoneNumber,
                    editingComponent: <NonEditableText>{detailsData.phoneNumber}</NonEditableText>,
                  },
                  {
                    label: t("pages.docSubmissionDetails.g2Status"),
                    displayComponent: detailsData?.g2Status
                      ? t(`commons.clientDocSubmissionStatus.${detailsData.g2Status}`)
                      : "-",
                    editingComponent: (
                      <NonEditableText>
                        {detailsData?.g2Status ? t(`commons.clientDocSubmissionStatus.${detailsData.g2Status}`) : "-"}
                      </NonEditableText>
                    ),
                  },
                  {
                    label: t("pages.docSubmissionDetails.requestTime"),
                    displayComponent: renderDate({ date: moment(detailsData.requestTime) }),
                    editingComponent: (
                      <NonEditableText>{renderDate({ date: moment(detailsData.requestTime) })}</NonEditableText>
                    ),
                  },
                  {
                    label: t("pages.docSubmissionDetails.lastStatus"),
                    displayComponent: !detailsData.lastStatusTime
                      ? "-"
                      : renderDate({ date: moment(detailsData.lastStatusTime) }),
                    editingComponent: (
                      <NonEditableText>
                        {!detailsData.lastStatusTime ? "-" : renderDate({ date: moment(detailsData.lastStatusTime) })}
                      </NonEditableText>
                    ),
                  },
                ]);

                rows.push.apply(rows, [
                  {
                    label: <FormTitle margin={32}>{t("pages.docSubmissionDetails.agentInfo.agentInfo")}</FormTitle>,
                    displayComponent: (
                      <FormRowWrapper ref={agentInfoRef}>
                        <NonEditableText
                          style={{
                            fontWeight: "bold",
                            fontSize: "16px",
                            color: styleTheme.palette.greyDarker,
                          }}
                        >
                          {t("pages.docSubmissionDetails.agentInfo.operatorId")}
                        </NonEditableText>

                        <NonEditableText>{detailsData.agent.operatorId}</NonEditableText>
                      </FormRowWrapper>
                    ),
                    editingComponent: (
                      <FormRowWrapper ref={agentInfoRef}>
                        <NonEditableText
                          style={{
                            fontWeight: "bold",
                            fontSize: "16px",
                            color: styleTheme.palette.greyDarker,
                          }}
                        >
                          {t("pages.docSubmissionDetails.agentInfo.operatorId")}
                        </NonEditableText>

                        <NonEditableText>{detailsData.agent.operatorId}</NonEditableText>
                      </FormRowWrapper>
                    ),
                  },
                  {
                    label: t("pages.docSubmissionDetails.agentInfo.shortcode"),
                    displayComponent: <NonEditableText>{detailsData.agent.shortcode}</NonEditableText>,
                    editingComponent: <NonEditableText>{detailsData.agent.shortcode}</NonEditableText>,
                  },
                  {
                    label: t("pages.docSubmissionDetails.firstName"),
                    displayComponent: <NonEditableText>{detailsData.agent.shortcode}</NonEditableText>,
                    editingComponent: <NonEditableText>{detailsData.agent.firstName}</NonEditableText>,
                  },
                  {
                    label: t("pages.docSubmissionDetails.agentInfo.middleName"),
                    displayComponent: <NonEditableText>{detailsData.agent.shortcode}</NonEditableText>,
                    editingComponent: <NonEditableText>{detailsData.agent.middleName}</NonEditableText>,
                  },
                  {
                    label: t("pages.docSubmissionDetails.lastName"),
                    displayComponent: <NonEditableText>{detailsData.agent.shortcode}</NonEditableText>,
                    editingComponent: <NonEditableText>{detailsData.agent.lastName}</NonEditableText>,
                  },
                ]);
                rows.push.apply(rows, [
                  {
                    label: <FormTitle margin={40}>{t("pages.docSubmissionDetails.status")}</FormTitle>,
                    displayComponent: (
                      <FormRowWrapper ref={formRowRef}>
                        <NonEditableText
                          style={{
                            fontWeight: "bold",
                            fontSize: "16px",
                            color: styleTheme.palette.greyDarker,
                          }}
                        >
                          {t("pages.docSubmissionDetails.validationStatus")}
                        </NonEditableText>

                        <NonEditableText
                          style={{
                            color: DocValidationUtils.getClientDocStatusColor(detailsData.status),
                          }}
                        >
                          {String(t(`commons.clientDocSubmissionStatus.${detailsData.status}`))}
                          {detailsData.g2Error && <G2ErrorMessage> - {detailsData.g2Error}</G2ErrorMessage>}
                        </NonEditableText>
                      </FormRowWrapper>
                    ),
                    editingComponent: (
                      <FormRowWrapper ref={formRowRef}>
                        <NonEditableText
                          style={{
                            fontWeight: "bold",
                            fontSize: "16px",
                            color: styleTheme.palette.greyDarker,
                          }}
                        >
                          {t("pages.docSubmissionDetails.validationStatus")}
                        </NonEditableText>

                        <NonEditableText
                          style={{
                            color: DocValidationUtils.getClientDocStatusColor(detailsData.status),
                          }}
                        >
                          {String(t(`commons.clientDocSubmissionStatus.${detailsData.status}`))}
                          {detailsData.g2Error && <G2ErrorMessage> - {detailsData.g2Error}</G2ErrorMessage>}
                        </NonEditableText>
                      </FormRowWrapper>
                    ),
                  },
                ]);

                if (isEditModeClicked) {
                  rows.push({
                    label: t("pages.docSubmissionDetails.comment"),
                    displayComponent: "",
                    editingComponent: (
                      <CommentWrapper>
                        <CustomErrorMessage>
                          <TextInput
                            name="comment"
                            id={"comment"}
                            onChange={e => {
                              const { length: customReasonLength } = e.target.value.trim();
                              setIsButtonDisabled(
                                customReasonLength <= CUSTOM_REASON_CHARACTERS_LIMIT.min ||
                                  customReasonLength >= CUSTOM_REASON_CHARACTERS_LIMIT.max,
                              );
                              handleChange(e);
                            }}
                            value={values.comment}
                            style={{ width: "100%", paddingRight: "50px" }}
                            maxLength={CUSTOM_REASON_CHARACTERS_LIMIT.max}
                            minLength={CUSTOM_REASON_CHARACTERS_LIMIT.min}
                          />
                        </CustomErrorMessage>
                      </CommentWrapper>
                    ),
                  });
                }

                if (
                  role !== "view_only" &&
                  !isEditModeClicked &&
                  showNextDocStatus &&
                  DocValidationDetailsUtils.getNextDocStatus(detailsData.status).length > 0 &&
                  (!DocValidationDetailsUtils.nextDocStatusHasReason(values.nextStatus) ||
                    DocValidationDetailsUtils.getNextDocStatus(detailsData.status).length !== 0) &&
                  firstOperator !== user.username
                ) {
                  rows.push.apply(rows, [
                    {
                      label: "",
                      displayComponent: detailsData.status === "REJECTED" && (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <FormRowWrapper ref={registrationApprovalRef}>
                            <div>
                              <NonEditableText
                                style={{
                                  fontWeight: "bold",
                                  fontSize: "16px",
                                  color: styleTheme.palette.greyDarker,
                                  marginRight: "130px",
                                }}
                              >
                                {t("pages.docSubmissionDetails.rejectReason")}
                              </NonEditableText>
                            </div>
                            <div>
                              <NonEditableText>{detailsData.reason}</NonEditableText>
                            </div>
                          </FormRowWrapper>
                        </div>
                      ),
                      editingComponent: (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <FormRowWrapper ref={registrationApprovalRef}>
                            {DocValidationDetailsUtils.getNextDocStatus(detailsData.status).length > 0 && (
                              <div style={{ flex: 1 }}>
                                <AutoClosingDropdown
                                  options={DocValidationDetailsUtils.getNextDocStatus(detailsData.status).map(
                                    status => {
                                      return {
                                        labelElement: (
                                          <NextStatus>
                                            <DocStatusItem status={status} />
                                            <span>{`${t(`commons.clientDocSubmissionStatus.${status}`)}`}</span>
                                          </NextStatus>
                                        ),
                                        key: status,
                                        label: "",
                                        disabled: DocValidationDetailsUtils.disableOption({
                                          optionToBeDisabled: ClientDocSubmissionNextStatusEnum.MOVE_ON,
                                          currentOption: status,
                                          currentStatus: detailsData.status,
                                          forbiddenStatuses: ["ERROR_ON_G2", "ERROR_ON_ID_DUPLICATIONS"],
                                        }),
                                      };
                                    },
                                  )}
                                  dropdownType={DropdownType.RECTANGULAR_NORMAL}
                                  selectOption={opt => {
                                    setFieldValue("nextStatusReason", undefined);
                                    setFieldValue("reasonText", undefined);
                                    setFieldValue("nextStatus", opt.key);
                                    setIsButtonDisabled(opt.key !== ClientDocSubmissionNextStatusEnum.MOVE_ON);
                                  }}
                                  label={
                                    <NextStatus>
                                      <DocStatusItem status={values.nextStatus} />
                                      <span>
                                        {t(
                                          `commons.clientDocSubmissionStatus.${values.nextStatus || "approvalStatus"}`,
                                        )}
                                      </span>
                                    </NextStatus>
                                  }
                                  hasValue={!!values.nextStatus}
                                />
                              </div>
                            )}
                            {!DocValidationDetailsUtils.nextDocStatusHasReason(values.nextStatus) ||
                              (DocValidationDetailsUtils.getNextDocStatus(detailsData.status).length !== 0 && (
                                <ErrorReason>
                                  <AutoClosingDropdown
                                    options={DocValidationDetailsUtils.getDocStatusReasons(values.nextStatus).map(
                                      reason => {
                                        return {
                                          label: tMarket(`statusReasons.${values.nextStatus}.${reason}`),
                                          key: reason,
                                        };
                                      },
                                    )}
                                    dropdownType={DropdownType.RECTANGULAR_NORMAL}
                                    selectOption={opt => {
                                      setFieldValue("nextStatusReason", opt.key);
                                      setIsButtonDisabled(opt.key === "CUSTOM");
                                    }}
                                    label={
                                      values.nextStatusReason
                                        ? tMarket(getStatusReason(values))
                                        : t(`pages.docSubmissionDetails.selectReason`)
                                    }
                                    hasValue={!!values.nextStatusReason}
                                  />
                                </ErrorReason>
                              ))}
                          </FormRowWrapper>
                          {values.nextStatusReason === "CUSTOM" && (
                            <>
                              <br />
                              <CustomErrorMessage>
                                <TextArea
                                  name="reasonText"
                                  placeholder={t("pages.docSubmissionDetails.typeReason")}
                                  autoComplete="off"
                                  verticalResize={false}
                                  horizontalResize={false}
                                  onChange={e => {
                                    const { length: customReasonLength } = e.target.value.trim();
                                    setIsButtonDisabled(
                                      customReasonLength <= CUSTOM_REASON_CHARACTERS_LIMIT.min ||
                                        customReasonLength >= CUSTOM_REASON_CHARACTERS_LIMIT.max,
                                    );
                                    handleChange(e);
                                  }}
                                  value={values.reasonText}
                                  color={styleTheme.palette.midGrey}
                                  maxLength={CUSTOM_REASON_CHARACTERS_LIMIT.max}
                                  minLength={CUSTOM_REASON_CHARACTERS_LIMIT.min}
                                />
                              </CustomErrorMessage>
                            </>
                          )}
                        </div>
                      ),
                    },
                  ]);
                }
              }

              return (
                <FormContainer>
                  <form onSubmit={handleSubmit}>
                    <FormSection
                      isEditing={isEditable && !isSubmittingReq}
                      title={
                        <div>
                          <div>
                            {t("pages.docSubmissionDetails.title")}{" "}
                            <span style={{ color: styleTheme.palette.greyDarker }}>
                              {requestDetailsParams.remoteId}
                            </span>
                          </div>
                          <div style={{ marginTop: "8px" }}>
                            {t("pages.docSubmissionDetails.operationType")}{" "}
                            <span style={{ color: styleTheme.palette.digitalDarkRed, fontWeight: "bold" }}>
                              {t(`pages.docSubmissionDetails.operationTypeOptions.${detailsData.operationType}`)}
                            </span>
                          </div>
                          <FormTitle margin={16}>
                            <strong>{t("pages.docSubmissionDetails.submittedInfo")}</strong>
                          </FormTitle>
                        </div>
                      }
                      rows={rows}
                    />
                  </form>
                </FormContainer>
              );
            }}
          />
          {isLoadingDetails ? <LoadingText>{t("commons.loading")}</LoadingText> : <></>}
        </MainContent>
        {isLoadingImages || hasLoadFailed ? (
          <>
            <LoadingText>{t("commons.loading")}</LoadingText>
          </>
        ) : (
          <SideMenu>
            <Section>
              <FormLabel>{t("pages.docSubmissionDetails.idPhotos")}</FormLabel>
              <ClientDocPhotos photoUrls={submissionImages} msisdn={detailsData.phoneNumber} />
            </Section>

            {additionalImages && additionalImages.length > 0 && (
              <Section>
                <FormLabel>{t("pages.docSubmissionDetails.additionalPhotos")}</FormLabel>
                <ClientAdditionalDocPhotos photoUrls={additionalImages} msisdn={detailsData.phoneNumber} />
              </Section>
            )}
          </SideMenu>
        )}
        {isLoadingDetails || hasLoadFailed ? (
          <></>
        ) : (
          <>
            {(detailsData.duplicateDocuments || detailsData.duplicateFaces) && (
              <DuplicatedFaceContainer>
                <DuplicatedFaceIcon color={styleTheme.palette.digitalRed} style={{ marginRight: 5 }}>
                  <ExclamationIcon />
                </DuplicatedFaceIcon>
                {detailsData.duplicateFaces && (
                  <>
                    <DuplicatedFaceText>{t("pages.docSubmissionDetails.duplicatedFace")}</DuplicatedFaceText>
                    <DuplicatedRegistrationId
                      onClick={() => {
                        setRequestDetailsParams((prev: IClientDocSubmissionDetailsRequest) => ({
                          ...prev,
                          remoteId: detailsData.duplicateFaces ? detailsData.duplicateFaces[0] : "",
                          previousDuplicates: [...prev.previousDuplicates, prev.remoteId],
                        }));
                      }}
                    >
                      {t("pages.docSubmissionDetails.registration")}
                    </DuplicatedRegistrationId>
                  </>
                )}

                {detailsData.duplicateDocuments && (
                  <>
                    <DuplicatedFaceText>{t("pages.docSubmissionDetails.duplicatedIdNumber")}</DuplicatedFaceText>
                    <DuplicatedRegistrationId
                      onClick={() => {
                        setRequestDetailsParams((prev: IClientDocSubmissionDetailsRequest) => ({
                          ...prev,
                          remoteId: detailsData.duplicateDocuments ? detailsData.duplicateDocuments[0] : "",
                          previousDuplicates: [...prev.previousDuplicates, prev.remoteId],
                        }));
                      }}
                    >
                      {t("pages.docSubmissionDetails.registration")}
                    </DuplicatedRegistrationId>
                  </>
                )}
              </DuplicatedFaceContainer>
            )}

            {detailsData.statusHistory?.length > 0 && (
              <HistoryListContainer>
                <DocStatusHistoryList historyEntries={detailsData.statusHistory} />
              </HistoryListContainer>
            )}

            {detailsData.changeHistories?.length > 0 && (
              <HistoryListContainer>
                <DocTaskHistoryList changeHistories={detailsData.changeHistories} identities={identities} />
              </HistoryListContainer>
            )}

            {detailsData.riskRatingHistory?.length > 0 && (
              <HistoryListContainer>
                <DocRiskRatingHistoryList riskRatingEntries={detailsData.riskRatingHistory} />
              </HistoryListContainer>
            )}
          </>
        )}
      </PageContent>
    </PageContainer>
  );
};

export default DocValidationDetailsPage;

const DuplicatedFaceContainer = styled("div")<{ color?: string }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-top 40px;
  flex: 1 0 100%;

  svg {
    width: 16px;
    height: 16px;
    stroke: ${props => (props.color ? props.color : props.theme.palette.midGrey)};
  }
`;

const DuplicatedFaceIcon = styled("div")<{ color?: string }>`
  width: 20px;
  height: 20px;
  stroke: ${props => (props.color ? props.color : props.theme.palette.midGrey)};
  background-color: #e6000014;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
    stroke: ${props => (props.color ? props.color : props.theme.palette.midGrey)};
  }
`;

const DuplicatedFaceText = styled("span")`
  font-family: Vodafone Rg;
`;

const DuplicatedRegistrationId = styled("span")`
  font-family: Vodafone Rg;
  text-decoration: underline;
  color: ${styleTheme.palette.turquoiseBlue};
  margin-left: 5px;
  cursor: pointer;
`;

const PageContent = styled("div")`
  display: flex;
  flex-direction: row;
  max-width: 100%;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: baseline;
  margin-top: calc(128px + 28px - 50px);
  margin-left: calc(200px + 20px + 24px - 48px);
  column-gap: 30px;

  @media (max-width: 1024px) {
    margin-left: auto;
  }
`;

const HistoryListContainer = styled("div")`
  flex: 1 0 100%;
  margin-top: 23px;

  @media (max-width: 805px) {
    border-top: 1px solid #ebebeb;
    margin-top: 40px;
  }
`;

const MainContent = styled("div")`
  display: flex;
  flex-direction: column;
  width: 60%;

  @media (max-width: 1024px) {
    flex-grow: 1;
  }
`;

const SideMenu = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 30px; /* spacing between sections */

  @media (max-width: 805px) {
    margin-top: 40px;
  }
`;

const FormContainer = styled("div")`
  .status-container {
    display: flex;
    flex-direction: column;
    align-items: self-start;
    justify-content: space-between;
    padding-bottom: 15px !important;

    div {
      max-width: 100% !important;
      width: 100% !important;
    }

    > div > div {
      margin-top: 26px;
    }
  }
`;

const FormRowWrapper = styled("div")`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  font-family: "Vodafone Rg";
  font-size: 16px;

  > span {
    flex: 1;
  }

  > div > button {
    width: 268px;
    height: 36px;
  }

  > div > div > input {
    width: 230px;
    border-color: ${styleTheme.palette.aluminium};
    cursor: pointer;

    &:hover {
      border-color: ${styleTheme.palette.greyDarker};
    }
  }
`;

const NextStatus = styled("div")`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: unset;

  svg {
    width: 18px;
    height: 18px;
    margin-right: 8px;
    stroke: ${styleTheme.palette.digitalGreen};
  }
`;

const ErrorReason = styled("div")`
  display: flex;
  align-items: center;
  flex: 1;

  button {
    height: 36px;
  }

  span {
    margin-left: 10px;
  }

  @media (max-width: 660px) {
    flex: 1 0 100%;
    margin-top: 14px;
  }
`;

const CustomErrorMessage = styled("div")`
  textarea {
    width: 100&;
    height: 50px;
    color: ${styleTheme.palette.black};
    border: 1px solid ${styleTheme.palette.midGrey};
  }

  textarea::placeholder {
    color: ${styleTheme.palette.midGrey};
  }
`;

const Section = styled("div")`
  display: flex;
  flex-direction: column;
`;

const FormLabel = styled("span")`
  display: block;
  font-family: Vodafone Rg;
  font-size: 16px;
  font-weight: bold;
  color: ${styleTheme.palette.midGrey};
  margin-bottom: 10px; /* spacing below label */
`;

const FormTitle = styled("div")<{ margin: number }>`
  font-size: 16px;
  font-weight: bold;
  font-family: "Vodafone Rg";
  margin-top: ${({ margin }) => margin}px;
`;

const ButtonsContainer = styled("div")`
  position: fixed;
  z-index: 1;
  right: 82px;
  top: 48px;
`;

const ButtonWrapper = styled("div")`
  width: fit-content;
  display: inline-block;
  margin-left: 12px;
  vertical-align: middle;
`;

const NonEditableText = styled("span")`
  font-family: Vodafone Rg;
  font-size: 16px;
  color: ${styleTheme.palette.darkGrey};
  align-self: center;
`;

const WarningHeader = styled("div")`
  font-family: Vodafone Rg;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  padding: 5px;
  margin-bottom: -80px;
  margin-top: calc(128px - 50px);
  margin-left: -50px;
  margin-right: -50px;
  border: 1px solid #eb8b00;
  background-color: #fff9c4;
  color: #eb8b00;
`;

const ManualEntryWrapper = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
`;

const ManualEntryLabel = styled("div")`
  margin-right: 7px;
  font-family: Vodafone Rg;
  color: ${props => (props.color ? props.color : styleTheme.palette.turquoiseBlue)};
`;

const G2ErrorMessage = styled("span")`
  color: black !important;
  font-family: Vodafone Rg;
`;

const MessageWrapper = styled("div")`
  display: flex;
  align-items: center;
`;

const EditableWrapper = styled("div")`
  position: relative;
  width: 100%;
`;

const EditContainer = styled("div")`
  position: absolute;
  right: 10px;
  top: 40%;
  transform: translateY(-50%);
  display: flex;
  gap: 5px;
`;

const Edit = styled("div")`
  display: flex;
  font-family: "Vodafone Rg";
  font-size: 14px;
  color: ${props => props.theme.palette.darkGrey};
  cursor: pointer;
  margin-left: "5px";
`;

const SmallButtonIconContainer = styled("div")<{ color?: string }>`
  width: 16px;
  height: 16px;
  stroke: ${props => (props.color ? props.color : props.theme.palette.midGrey)};

  svg {
    width: 23px;
    height: 23px;
    stroke: ${props => (props.color ? props.color : props.theme.palette.midGrey)};
  }
`;

const NoEditableWrapper = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CommentWrapper = styled("div")`
  display: flex;
  flex-direction: column;
`;

const DropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 9999;
  border: 1px solid ${styleTheme.palette.aluminium};
  border-radius: 4px;
  max-height: 150px;
  overflow-y: auto;
  background-color: white;
  font-size: 14px;
  color: ${styleTheme.palette.darkGrey};
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  font-family: "Vodafone Rg", sans-serif;
`;

const DropdownItem = styled.div<{ selected?: boolean; disabled?: boolean }>`
  padding: 10px 12px;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  background-color: ${({ selected }) => (selected ? "#f4f4f4" : "#fff")};
  color: ${({ disabled }) => (disabled ? styleTheme.palette.silver : "inherit")};
  font-family: "Vodafone Rg", sans-serif;

  &:hover {
    background-color: ${({ disabled }) => (disabled ? "white" : "#f9f9f9")};
  }

  &:last-child {
    border-bottom: none;
  }
`;
const ValidationContainer = styled.div`
  min-height: 18px;
  margin-top: 4px;
`;

const ValidationErrorFloating = styled.div`
  position: absolute;
  bottom: 1px;
  left: 0;
  font-size: 13px;
  color: red;
  line-height: 1;
  white-space: nowrap;
  font-family: Vodafone Rg;
`;
